if (!customElements.get("product-configurator")) {
  class ProductConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.selectors = {
        form: this.querySelector("form"),
        product: this.querySelector('[name="product-id"]'),
        handle: this.querySelector('[name="product-handle"]'),
        variant: this.querySelector('[name="id"]'),
        quantity: this.querySelector('[name="quantity"]'),
        status: this.querySelector("[data-pdp-status]"),
        fabricLabel: this.querySelector("[data-pdp-fabric-label]"),
        fabricFamilyLabel: this.querySelector("[data-pdp-fabric-family-label]"),
        fabricCategoryLabel: this.querySelector("[data-pdp-fabric-category-label]"),
        fabricColorLabel: this.querySelector("[data-pdp-fabric-color-label]"),
        fabricColorConnector: this.querySelector("[data-pdp-fabric-color-connector]"),
        footer: this.querySelector("[data-footer]"),
        footerPrice: this.querySelector("[data-footer-price]"),
      };

      if (!this.selectors.form || !this.selectors.variant) return;

      this.config = this.readConfig();
      if (!this.config) return;

      this.mode = this.config.mode || this.dataset.configuratorMode || "native";
      this.nativeOptionElements = Array.from(this.querySelectorAll("[data-pdp-native-option]"));
      this.fabricInputs = Array.from(this.querySelectorAll("[data-pdp-fabric]"));
      this.fabricFamilyCards = Array.from(this.querySelectorAll("[data-pdp-fabric-family-card]"));
      this.fabricFamilyPanels = Array.from(this.querySelectorAll("[data-pdp-fabric-family-panel]"));
      this.fabricFamilyScrollbars = new Map();
      if (typeof PerfectScrollbar === "function") {
        this.fabricFamilyPanels.forEach((panel) => {
          const scroller = panel.querySelector("[data-pdp-fabric-family-scroll]");
          if (!scroller) return;

          this.fabricFamilyScrollbars.set(panel, new PerfectScrollbar(scroller, {
            suppressScrollX: true,
            wheelPropagation: true,
            swipeEasing: true,
            minScrollbarLength: 0,
          }));
        });
      }
      this.fabricFamilyPriceElements = Array.from(this.querySelectorAll("[data-pdp-fabric-family-price]"));
      this.addonInputs = Array.from(this.querySelectorAll("[data-pdp-addon-choice]"));
      this.productAddonCards = Array.from(this.querySelectorAll("[data-product-addon-card]"));
      this.productAddonWarnings = new Set();
      this.siblingLinks = Array.from(this.querySelectorAll("[data-pdp-sibling-product]"));
      this.submitButtons = Array.from(this.querySelectorAll('button[type="submit"], [data-footer-submit]'));
      this.resolveToken = 0;
      this.isResolving = false;
      this.resolveError = false;
      this.cartError = "";
      this.addonRemovedNotice = "";
      this.announcedValidationMessage = "";
      // Last auto-dropped product-backed choice per group. Written only by
      // reconcileAddonState, cleared by any explicit input in the group, and
      // consumed by a live reconcile when the choice resolves again — history
      // navigation replays the URL exactly and never restores from here.
      this.droppedAddons = {};

      // Edit flow from the cart: edit_config carries the _config_key of the
      // lines to replace after a successful re-add, edit_qty the quantity to
      // carry over so editing does not silently reset it to 1.
      const editParams = new URLSearchParams(window.location.search);
      this.editConfigKey = editParams.get("edit_config") || "";
      this.editIwcKey = editParams.get("edit_iwc_key") || "";
      this.editLineKey = editParams.get("edit_line") || "";
      this.editOfferContext = null;
      this.editContextLoading = Boolean(this.editConfigKey);
      const editQuantity = Number(editParams.get("edit_qty") || 0);
      if (this.editConfigKey && this.selectors.quantity && Number.isFinite(editQuantity) && editQuantity > 0) {
        this.selectors.quantity.value = String(editQuantity);
      }
      this.isHandlingPopState = false;
      this.isApplyingExternalState = false;
      window.__productConfiguratorInstanceId = (window.__productConfiguratorInstanceId || 0) + 1;
      this.instanceId = `product-configurator-${window.__productConfiguratorInstanceId}`;
      this.adapters = this.createAdapters();
      this.adapter = this.adapters[this.mode] || this.adapters.native;
      this.state = this.initState();
      this.popStateHandler = () => this.handlePopState();
      this.configurationChangeHandler = (event) => this.handleExternalConfigurationChange(event);

      this.listen();
      this.adapter.init?.();
      this.renderAll();
      this.initNativeChoiceWidths();

      if (this.editConfigKey) {
        this.loadEditOfferContext().catch(() => {
          console.info("[IWC analytics] edit_context_unavailable");
        }).finally(() => {
          this.renderPrice();
          this.publishOfferView();
        });
      }

      if (this.adapter.shouldResolveOnInit?.() || this.state.optionValueIds.length > 0) {
        this.resolveVariant({ updateHistory: false, replaceHistory: true });
      } else {
        this.publishOfferView();
      }

      // Landing from the cart's edit link on a customizable product: open the
      // drawer where the configuration UI lives, once per page load.
      if (this.editConfigKey && !window.__pdpEditDrawerOpened && !window.Shopify?.designMode) {
        const configuratorDrawer = document.getElementById("drawer-product-configurator");
        if (configuratorDrawer) {
          window.__pdpEditDrawerOpened = true;
          customElements.whenDefined("drawer-section").then(() => {
            window.dispatchEvent(new CustomEvent("drawerOpen", { detail: { id: "drawer-product-configurator" } }));
          });
        }
      }
    }

    disconnectedCallback() {
      this.choiceResizeObserver?.disconnect();
      if (this.popStateHandler) {
        window.removeEventListener("popstate", this.popStateHandler);
      }
      if (this.configurationChangeHandler) {
        window.removeEventListener("pdpConfigurationChange", this.configurationChangeHandler);
      }
      if (this.optionDropdownOutsideHandler) {
        document.removeEventListener("click", this.optionDropdownOutsideHandler);
      }
      if (this.optionDropdownEscapeHandler) {
        document.removeEventListener("keydown", this.optionDropdownEscapeHandler);
      }
    }

    initNativeChoiceWidths() {
      const rows = this.nativeOptionElements
        .map((option) => option.querySelector('.pdp-option__values:not(.pdp-fabric-swatches, .pdp-option__values--list)'))
        .filter(Boolean);
      if (!rows.length) return;

      const updateWidths = () => {
        rows.forEach((row) => {
          const labels = Array.from(row.querySelectorAll(':scope > .pdp-choice > span'));
          if (!labels.length || !row.clientWidth) return;

          labels.forEach((label) => { label.style.width = ''; });
          const width = Math.min(row.clientWidth, Math.ceil(Math.max(...labels.map((label) => label.getBoundingClientRect().width))));
          labels.forEach((label) => { label.style.width = `${width}px`; });
        });
      };

      updateWidths();
      this.choiceResizeObserver = new ResizeObserver(updateWidths);
      rows.forEach((row) => this.choiceResizeObserver.observe(row));
      document.fonts?.ready.then(() => { if (this.isConnected) updateWidths(); });
    }

    readConfig() {
      const section = this.closest(".shopify-section");
      const script = section?.querySelector("[data-product-configurator-json]") ||
        document.querySelector("[data-product-configurator-json]");

      if (!script) return null;

      try {
        const config = JSON.parse(script.textContent || "{}");
        config.propertyLabels = config.propertyLabels || {};
        config.nativeOptions = config.nativeOptions || {};
        config.nativeOptions.options = Array.isArray(config.nativeOptions.options)
          ? config.nativeOptions.options
          : [];
        config.nativeOptions.optionValueIds = config.nativeOptions.optionValueIds || {};
        config.selectionGroups = Array.isArray(config.selectionGroups) ? config.selectionGroups : [];
        config.addonGroups = Array.isArray(config.addonGroups) ? config.addonGroups : [];
        config.siblingProducts = Array.isArray(config.siblingProducts) ? config.siblingProducts : [];
        config.media = config.media || {};
        config.media.items = Array.isArray(config.media.items) ? config.media.items : [];
        config.media.fabricMediaTokenMap = this.normalizeFabricMediaTokenMap(config.media.fabricMediaTokenMap);
        config.media.galleryFilterOptionOverrides = Array.isArray(config.media.galleryFilterOptionOverrides)
          ? config.media.galleryFilterOptionOverrides
          : [];
        return config;
      } catch (error) {
        console.error("[ProductConfigurator] Invalid configurator JSON", error);
        return null;
      }
    }

    parseUrlState() {
      const params = new URLSearchParams(window.location.search);
      const addons = {};

      params.forEach((value, key) => {
        if (!key.startsWith("ao_")) return;
        const handle = key.slice(3);
        addons[handle] = value || "none";
      });

      return {
        variantId: params.get("variant") || "",
        fabric: params.get("fabric") || "",
        sibling: params.get("sibling") || params.get("sibling_product") || "",
        optionValueIds: this.parseOptionValueIds(params.get("option_values")),
        addons,
      };
    }

    initState() {
      const urlState = this.parseUrlState();
      const nativeOptions = this.getInitialNativeOptions();

      if (urlState.optionValueIds.length) {
        this.applyOptionValueIdsToNativeState(urlState.optionValueIds, nativeOptions);
      }

      const state = {
        variant: this.config.currentVariant || null,
        nativeOptions,
        fabric: null,
        addons: {},
        sibling: urlState.sibling,
        optionValueIds: urlState.optionValueIds,
      };

      state.fabric = this.resolveInitialFabric(urlState, state);
      if (state.fabric) {
        this.applyFabricToNativeState(state.fabric, state.nativeOptions);
      }

      this.config.addonGroups.forEach((addonGroup) => {
        state.addons[addonGroup.handle] = this.getInitialAddonValue(addonGroup, urlState);
      });

      return state;
    }

    createAdapters() {
      const nativeAdapter = {
        getSelectedOptions: () => this.getNativeSelectedOptions(),
        renderChoices: () => {},
        shouldResolveOnInit: () => false,
      };

      return {
        native: nativeAdapter,
        upholstery_hybrid: {
          getSelectedOptions: () => {
            const selected = this.getNativeSelectedOptions();
            const priceGroupName = this.config.nativeOptions.priceGroupOptionName;
            if (this.state.fabric && priceGroupName) {
              selected[priceGroupName] = this.state.fabric.priceGroup;
            }
            return selected;
          },
          renderChoices: () => this.renderFabricChoices(),
          shouldResolveOnInit: () => true,
        },
      };
    }

    listen() {
      this.listenOptionDropdowns();

      this.nativeOptionElements.forEach((option) => {
        option.querySelectorAll("[data-pdp-native-value]").forEach((input) => {
          input.addEventListener("change", () => {
            if (!input.checked) return;
            this.state.nativeOptions[option.dataset.optionName] = input.value;
            this.reconcileGalleryPair(option.dataset.optionName);
            this.scheduleResolveVariant();
          });
        });
      });

      this.fabricInputs.forEach((input) => {
        input.addEventListener("change", () => {
          if (!input.checked) return;
          const fabric = this.getFabricChoice(input.value);
          this.state.fabric = fabric;
          if (fabric) {
            this.applyFabricToNativeState(fabric, this.state.nativeOptions);
          }
          this.reconcileGalleryPair("fabric");
          this.scheduleResolveVariant();
        });
      });

      this.querySelectorAll("[data-pdp-fabric-family-open]").forEach((button) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.openFabricFamilySelector(button.dataset.pdpFabricFamilyOpen || "");
        });
      });

      this.querySelectorAll("[data-pdp-fabric-family-close]").forEach((button) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();
          this.closeFabricFamilySelector();
        });
      });

      this.querySelectorAll("[data-pdp-fabric-family-confirm]").forEach((button) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();
          this.closeFabricFamilySelector();
        });
      });

      this.querySelectorAll("[data-pdp-fabric-family-color-option]").forEach((option) => {
        option.addEventListener("click", (event) => {
          event.preventDefault();
          this.selectFabricByHandle(option.dataset.fabricHandle || "");
        });

        option.addEventListener("keydown", (event) => {
          if (event.target !== option) return;
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          this.selectFabricByHandle(option.dataset.fabricHandle || "");
        });
      });

      this.querySelectorAll("[data-pdp-fabric-image-open]").forEach((button) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.openFabricImageModal(button);
        });

        button.addEventListener("keydown", (event) => {
          // Keep activation keys away from the color-option card underneath,
          // but let Escape reach the document so drawers still close on it.
          if (event.key === "Enter" || event.key === " ") event.stopPropagation();
        });
      });

      this.querySelectorAll("[data-pdp-fabric-image-modal]").forEach((modal) => {
        modal.querySelector("[data-pdp-fabric-image-close]")?.addEventListener("click", () => {
          this.closeFabricImageModal(modal);
        });

        modal.addEventListener("click", (event) => {
          if (event.target === modal) this.closeFabricImageModal(modal);
        });

        modal.addEventListener("close", () => {
          modal.returnFocus?.focus();
          modal.returnFocus = null;
        });

        modal.addEventListener("keydown", (event) => {
          // Escape closes only the zoom dialog; without this the document
          // keydown listener broadcasts escPressed and every drawer closes,
          // throwing the customer out of the configurator mid-flow.
          if (event.key === "Escape") event.stopPropagation();
        });
      });

      this.addEventListener("change", (event) => {
        const input = event.target.closest("[data-pdp-addon-choice]");
        if (!input) return;
        if (input.type === "radio" && !input.checked) return;
        const groupHandle = input.dataset.addonGroupHandle || "";
        if (!groupHandle) return;
        this.state.addons[groupHandle] = input.value || "none";
        // An explicit choice supersedes the auto-drop memory for the group.
        delete this.droppedAddons[groupHandle];
        this.addonRemovedNotice = "";
        this.renderAddons();
        this.renderPrice();
        this.renderStatus();
        this.updateUrl();
        this.dispatchConfigurationChange(this.resolveGallery(), { variantChange: false });
        this.publishOfferView();
      });

      this.siblingLinks.forEach((link) => {
        link.addEventListener("click", (event) => this.handleSiblingClick(event, link));
      });

      this.selectors.form.addEventListener("submit", (event) => {
        event.preventDefault();
        this.addToCart();
      });

      this.querySelectorAll("[data-footer-submit]").forEach((button) => {
        button.addEventListener("click", () => this.addToCart());
      });

      window.addEventListener("popstate", this.popStateHandler);
      window.addEventListener("pdpConfigurationChange", this.configurationChangeHandler);
    }

    getInitialNativeOptions() {
      const selected = Object.assign({}, this.config.currentVariant?.selectedOptions || {});

      this.optionConfigs().forEach((option) => {
        if (!selected[option.name]) {
          selected[option.name] = option.selectedValue || option.values?.[0]?.name || "";
        }
      });

      this.nativeOptionElements.forEach((option) => {
        const checked = option.querySelector("[data-pdp-native-value]:checked");
        if (checked) {
          selected[option.dataset.optionName] = checked.value;
        }
      });

      return selected;
    }

    resolveInitialFabric(urlState, state) {
      if (this.mode !== "upholstery_hybrid") return null;

      const group = this.getSelectionGroup("upholstery");
      const choices = group?.choices || [];
      const fromUrl = choices.find((choice) => choice.handle === urlState.fabric);
      if (fromUrl) return this.hydrateFabricChoice(fromUrl);

      const checkedInput = this.fabricInputs.find((input) => input.checked);
      const fromDom = checkedInput ? choices.find((choice) => choice.handle === checkedInput.value) : null;
      if (fromDom) return this.hydrateFabricChoice(fromDom);

      const priceGroupName = this.config.nativeOptions.priceGroupOptionName;
      const selectedPriceGroup = state.nativeOptions[priceGroupName];
      const fromPriceGroup = choices.find((choice) => {
        return this.normalizeGroup(choice.priceGroup) === this.normalizeGroup(selectedPriceGroup);
      });
      if (fromPriceGroup) return this.hydrateFabricChoice(fromPriceGroup);

      return choices[0] ? this.hydrateFabricChoice(choices[0]) : null;
    }

    applyOptionValueIdsToNativeState(optionValueIds, nativeOptions) {
      const wanted = new Set(optionValueIds.map((id) => this.normalizeId(id)));

      this.optionConfigs().forEach((option) => {
        const value = option.values?.find((item) => wanted.has(this.normalizeId(item.id)));
        if (value) nativeOptions[option.name] = value.name;
      });
    }

    applyFabricToNativeState(fabric, nativeOptions) {
      const priceGroupName = this.config.nativeOptions.priceGroupOptionName;
      if (priceGroupName && fabric?.priceGroup) {
        nativeOptions[priceGroupName] = fabric.priceGroup;
      }
    }

    scheduleResolveVariant() {
      clearTimeout(this.resolveTimer);
      this.isHandlingPopState = false;
      this.resolveError = false;
      this.cartError = "";
      this.addonRemovedNotice = "";
      this.resolveToken += 1;
      this.setResolving(true);
      this.resolveTimer = setTimeout(() => {
        this.resolveVariant({ updateHistory: true });
      }, 120);
      this.renderAll();
    }

    resolveVariant(options = {}) {
      // The whole prelude is guarded: a synchronous throw before the promise
      // exists would otherwise leave isResolving stuck and the button dead
      // until reload. Every caller gets a settled promise either way.
      try {
        const selectedOptions = this.adapter.getSelectedOptions();
        const optionValueIds = this.getSelectedOptionValueIds(selectedOptions);
        const token = ++this.resolveToken;

        this.state.optionValueIds = optionValueIds;
        this.resolveError = false;
        this.cartError = "";
        this.addonRemovedNotice = "";

        if (!optionValueIds.length) {
          this.state.variant = this.config.currentVariant || null;
          this.reconcileAddonState();
          this.renderAll();
          if (options.updateHistory) this.updateUrl({ replace: options.replaceHistory });
          this.setResolving(false);
          return Promise.resolve(this.state.variant);
        }

        this.setResolving(true);

        return this.fetchSectionConfig({ optionValueIds })
          .then((config) => {
            if (token !== this.resolveToken) return null;
            const previousConfig = this.config;
            this.config = config;
            this.state.variant = config.currentVariant || null;
            this.reconcileAddonState(previousConfig);
            this.renderAll();
            if (options.updateHistory || options.replaceHistory) {
              this.updateUrl({ replace: options.replaceHistory });
            }
            return this.state.variant;
          })
          .catch((error) => {
            if (token === this.resolveToken) {
              console.error("[ProductConfigurator] Variant resolver failed", error);
              this.resolveError = true;
              this.renderAll();
            }
            return null;
          })
          .finally(() => {
            if (token === this.resolveToken) this.setResolving(false);
          });
      } catch (error) {
        console.error("[ProductConfigurator] Variant resolver failed", error);
        this.resolveError = true;
        this.setResolving(false);
        this.renderAll();
        return Promise.resolve(null);
      }
    }

    // Docs 11.4.4: a hung request must not leave the UI pending forever. The
    // abort rejects the fetch promise, so the existing catch paths render
    // their retryable statuses instead of a permanently disabled button.
    fetchWithTimeout(url, options = {}, timeoutMs = 15000) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), timeoutMs);

      return fetch(url, Object.assign({}, options, { signal: controller.signal }))
        .finally(() => clearTimeout(timeout));
    }

    fetchSectionConfig({ optionValueIds, variantId } = {}) {
      const url = new URL(this.config.product?.url || window.location.pathname, window.location.origin);

      if (optionValueIds?.length) {
        url.searchParams.set("option_values", optionValueIds.join(","));
      } else if (variantId) {
        url.searchParams.set("variant", variantId);
      }

      // Dedicated JSON-only section (sections/configurator-json.liquid): the
      // resolver consumes canonical JSON exclusively, so re-rendering the full
      // main-product section (~12x the bytes and Liquid time) is pure waste.
      url.searchParams.set("section_id", "configurator-json");
      const requestKey = url.toString();

      // Inline and drawer instances resolve the same URL on init and on
      // popstate. Sharing the in-flight promise halves those requests while
      // each instance keeps its own fail-closed pending state; settled
      // requests are dropped so later resolves always fetch fresh data.
      const inFlight = window.__pdpSectionRequests = window.__pdpSectionRequests || new Map();
      const pending = inFlight.get(requestKey);
      if (pending) return pending;

      const request = this.fetchWithTimeout(requestKey)
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const script = doc.querySelector("[data-product-configurator-json]");
          if (!script) throw new Error("Missing configurator JSON in section response");
          const nextConfig = JSON.parse(script.textContent || "{}");
          nextConfig.propertyLabels = nextConfig.propertyLabels || {};
          nextConfig.nativeOptions = nextConfig.nativeOptions || {};
          nextConfig.nativeOptions.options = Array.isArray(nextConfig.nativeOptions.options)
            ? nextConfig.nativeOptions.options
            : [];
          nextConfig.nativeOptions.optionValueIds = nextConfig.nativeOptions.optionValueIds || {};
          nextConfig.selectionGroups = Array.isArray(nextConfig.selectionGroups) ? nextConfig.selectionGroups : [];
          nextConfig.addonGroups = Array.isArray(nextConfig.addonGroups) ? nextConfig.addonGroups : [];
          nextConfig.siblingProducts = Array.isArray(nextConfig.siblingProducts) ? nextConfig.siblingProducts : [];
          nextConfig.media = nextConfig.media || {};
          nextConfig.media.items = Array.isArray(nextConfig.media.items) ? nextConfig.media.items : [];
          nextConfig.media.fabricMediaTokenMap = this.normalizeFabricMediaTokenMap(nextConfig.media.fabricMediaTokenMap);
          nextConfig.media.galleryFilterOptionOverrides = Array.isArray(nextConfig.media.galleryFilterOptionOverrides)
            ? nextConfig.media.galleryFilterOptionOverrides
            : [];
          return nextConfig;
        })
        .finally(() => inFlight.delete(requestKey));

      inFlight.set(requestKey, request);
      return request;
    }

    renderAll() {
      this.renderNativeOptions();
      this.renderChoices();
      this.renderAddons();
      this.renderPrice();
      this.renderVariantDetails();
      this.renderTechnicalParameterImages();
      this.renderFabricDetails();
      this.renderGallery();
      this.renderStatus();
    }

    renderChoices() {
      this.adapter.renderChoices?.();
      this.renderFabricFamilyCards();
      this.renderFabricFamilyPrices();
      this.renderSiblingProducts();
    }

    openFabricFamilySelector(familyHandle) {
      const step = this.querySelector(".product-configurator__step--fabrics");
      const panel = this.fabricFamilyPanels.find((item) => item.dataset.familyHandle === familyHandle);
      if (!step || !panel) return;

      this.classList.add("product-configurator--fabric-selector-open");
      step.classList.add("product-configurator__step--fabric-selector-open");
      step.dataset.fabricSelectorFamily = familyHandle;
      this.fabricFamilyPanels.forEach((item) => {
        item.classList.remove("is-open");
        item.hidden = item !== panel;
      });

      const scroller = panel.querySelector("[data-pdp-fabric-family-scroll]");
      if (scroller) {
        scroller.scrollTop = 0;
      }

      panel.getBoundingClientRect();
      panel.classList.add("is-open");
      this.fabricFamilyScrollbars.get(panel)?.update();
    }

    closeFabricFamilySelector() {
      this.classList.remove("product-configurator--fabric-selector-open");
      const step = this.querySelector(".product-configurator__step--fabrics");
      if (step) {
        step.classList.remove("product-configurator__step--fabric-selector-open");
        delete step.dataset.fabricSelectorFamily;
      }
      this.fabricFamilyPanels.forEach((panel) => {
        if (panel.hidden) return;

        panel.classList.remove("is-open");
        const hidePanel = () => {
          if (!panel.classList.contains("is-open")) {
            panel.hidden = true;
          }
        };
        const handleTransitionEnd = (event) => {
          if (event.propertyName !== "transform") return;
          panel.removeEventListener("transitionend", handleTransitionEnd);
          hidePanel();
        };

        panel.addEventListener("transitionend", handleTransitionEnd);
        window.setTimeout(() => {
          panel.removeEventListener("transitionend", handleTransitionEnd);
          hidePanel();
        }, 450);
      });
    }

    openFabricImageModal(trigger) {
      const panel = trigger.closest("[data-pdp-fabric-family-panel]");
      const modal = panel?.querySelector("[data-pdp-fabric-image-modal]");
      const image = modal?.querySelector("[data-pdp-fabric-image-modal-image]");
      const imageSrc = trigger.dataset.imageSrc || "";
      if (!modal || !image || !imageSrc || typeof modal.showModal !== "function") return;

      image.src = imageSrc;
      image.alt = trigger.dataset.imageAlt || "";
      modal.returnFocus = trigger;
      if (!modal.open) modal.showModal();
    }

    closeFabricImageModal(modal) {
      if (modal?.open) modal.close();
    }

    selectFabricByHandle(handle) {
      const input = this.fabricInputs.find((item) => item.value === handle);
      if (!input || input.disabled) return;

      if (input.checked) {
        this.renderFabricFamilyCards();
        return;
      }

      input.checked = true;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }

    renderFabricFamilyCards() {
      if (!this.fabricFamilyCards.length && !this.fabricFamilyPanels.length) return;

      this.fabricFamilyCards.forEach((card) => {
        const familyHandle = card.dataset.familyHandle || "";
        const selectedInput = this.getSelectedFabricInputForFamily(familyHandle);
        if (!selectedInput) return;

        card.querySelectorAll("[data-pdp-fabric-family-card-label]").forEach((label) => {
          label.setAttribute("for", selectedInput.id);
        });

        this.updateFabricFamilyImage(
          card.querySelector("[data-pdp-fabric-family-image]"),
          selectedInput,
          card.querySelector("[data-pdp-fabric-family-fill]"),
        );
        this.updateFabricFamilySwatch(
          card.querySelector("[data-pdp-fabric-family-swatch]"),
          selectedInput,
          card.querySelector("[data-pdp-fabric-family-swatch-fill]"),
        );

        const colorLabel = card.querySelector("[data-pdp-fabric-family-color-label]");
        if (colorLabel) {
          const colorLabelText = selectedInput.dataset.fabricColorLabel || selectedInput.dataset.fabricName || "";
          colorLabel.textContent = colorLabelText;
          colorLabel.closest(".product-configurator__fabric-color-label")?.setAttribute("title", colorLabelText);
        }

        const badge = card.querySelector("[data-pdp-fabric-family-badge]");
        const badgeSource = card.querySelector(`[data-fabric-badge-source="${this.escapeAttribute(selectedInput.value)}"]`);
        if (badge) {
          const badgeLabel = badgeSource?.dataset.badgeLabel || "";
          const badgeColor = badgeSource?.dataset.badgeColor || "";

          badge.textContent = badgeLabel;
          badge.hidden = !badgeLabel;
          if (badgeColor) {
            badge.style.backgroundColor = badgeColor;
          } else {
            badge.style.removeProperty("background-color");
          }
        }
      });

      this.fabricFamilyPanels.forEach((panel) => {
        const familyHandle = panel.dataset.familyHandle || "";
        const selectedInput = this.getSelectedFabricInputForFamily(familyHandle);
        if (!selectedInput) return;

        this.updateFabricFamilyImage(
          panel.querySelector("[data-pdp-fabric-family-panel-image]"),
          selectedInput,
          panel.querySelector("[data-pdp-fabric-family-panel-fill]"),
        );

        const heroZoom = panel.querySelector("[data-pdp-fabric-family-hero-zoom]");
        if (heroZoom) {
          const zoomSrc = selectedInput.dataset.fabricZoomSrc || selectedInput.dataset.fabricImageSrc || "";
          heroZoom.dataset.imageSrc = zoomSrc;
          heroZoom.dataset.imageAlt = selectedInput.dataset.fabricName || "";
          heroZoom.hidden = !zoomSrc;
        }

        const details = panel.querySelector("[data-pdp-fabric-family-details]");
        if (details) {
          details.dataset.handle = selectedInput.value || selectedInput.dataset.fabricHandle || "";
        }

        const selectedLabel = panel.querySelector("[data-pdp-fabric-family-selected]");
        if (selectedLabel) {
          selectedLabel.textContent = selectedInput.dataset.fabricName || selectedInput.dataset.fabricColorLabel || "";
        }

        panel.querySelectorAll("[data-pdp-fabric-family-color-option]").forEach((option) => {
          const input = this.fabricInputs.find((item) => item.value === option.dataset.fabricHandle);
          const isSelected = option.dataset.fabricHandle === selectedInput.value;
          const isDisabled = Boolean(input?.disabled);

          option.classList.toggle("is-selected", isSelected);
          option.classList.toggle("is-disabled", isDisabled);
          option.setAttribute("aria-checked", String(isSelected));
          option.setAttribute("aria-disabled", String(isDisabled));
          option.tabIndex = isDisabled ? -1 : 0;
        });
      });
    }

    getSelectedFabricInputForFamily(familyHandle) {
      const inputs = this.fabricInputs.filter((input) => input.dataset.familyHandle === familyHandle);
      return inputs.find((input) => input.checked) || inputs[0] || null;
    }

    renderFabricFamilyPrices() {
      if (!this.fabricFamilyPriceElements.length) return;

      const familyPriceDiffs = this.getFabricFamilyPriceDiffs();
      if (!familyPriceDiffs.size) return;

      this.fabricFamilyPriceElements.forEach((element) => {
        const familyHandle = element.dataset.familyHandle || element.closest("[data-family-handle]")?.dataset.familyHandle || "";
        if (!familyHandle || !familyPriceDiffs.has(familyHandle)) return;

        element.textContent = this.formatPriceDifference(familyPriceDiffs.get(familyHandle));
      });
    }

    getFabricFamilyPriceDiffs() {
      const priceGroupOption = this.getOptionConfig(this.config.nativeOptions.priceGroupOptionName);
      if (!priceGroupOption) return new Map();

      const familyPriceGroups = new Map();
      this.fabricInputs.forEach((input) => {
        const familyHandle = input.dataset.familyHandle || "";
        const priceGroup = input.dataset.priceGroup || "";

        if (familyHandle && priceGroup && !familyPriceGroups.has(familyHandle)) {
          familyPriceGroups.set(familyHandle, priceGroup);
        }
      });

      const familyPrices = new Map();
      familyPriceGroups.forEach((priceGroup, familyHandle) => {
        const valueConfig = this.findOptionValue(priceGroupOption.name, priceGroup);
        const price = valueConfig?.price == null ? NaN : Number(valueConfig.price);

        if (Number.isFinite(price)) {
          familyPrices.set(familyHandle, price);
        }
      });

      if (!familyPrices.size) return new Map();

      const basePrice = Math.min(...familyPrices.values());
      const familyPriceDiffs = new Map();
      familyPrices.forEach((price, familyHandle) => {
        familyPriceDiffs.set(familyHandle, price - basePrice);
      });

      return familyPriceDiffs;
    }

    updateFabricFamilyImage(image, input, fill = null) {
      if (!input) return;
      const src = input.dataset.fabricImageSrc || "";
      if (image) {
        if (src) {
          if (image.getAttribute("src") !== src) {
            image.setAttribute("src", src);
          }
          image.setAttribute("alt", input.dataset.fabricName || "");
          image.hidden = false;
        } else {
          image.removeAttribute("src");
          image.hidden = true;
        }
      }
      this.updateFabricFamilyFill(fill, input, Boolean(src && image));
    }

    updateFabricFamilySwatch(image, input, fill = null) {
      if (!input) return;
      const src = input.dataset.fabricSwatchSrc || "";
      if (image) {
        if (src) {
          if (image.getAttribute("src") !== src) {
            image.setAttribute("src", src);
          }
          image.hidden = false;
        } else {
          image.removeAttribute("src");
          image.hidden = true;
        }
      }
      this.updateFabricFamilyFill(fill, input, Boolean(src && image));
    }

    updateFabricFamilyFill(fill, input, hasImage) {
      if (!fill) return;
      const color = input.dataset.fabricColor || "";
      if (color) {
        fill.style.background = color;
      } else {
        fill.style.removeProperty("background");
      }
      fill.hidden = hasImage;
    }

    listenOptionDropdowns() {
      this.optionDropdowns = Array.from(this.querySelectorAll("[data-pdp-option-select]"));
      if (!this.optionDropdowns.length) return;

      this.optionDropdowns.forEach((dropdown) => {
        const trigger = dropdown.querySelector("[data-pdp-option-select-trigger]");
        const panel = dropdown.querySelector("[data-pdp-option-select-panel]");
        if (!trigger || !panel) return;

        trigger.addEventListener("click", () => {
          const expanded = trigger.getAttribute("aria-expanded") === "true";
          this.closeOptionDropdowns();
          if (!expanded) this.toggleOptionDropdown(dropdown, true);
        });

        panel.addEventListener("change", (event) => {
          if (!event.target.matches("[data-pdp-native-value]")) return;
          this.toggleOptionDropdown(dropdown, false);
          trigger.focus();
        });

        panel.addEventListener("keydown", (event) => {
          if (event.key !== "Escape") return;
          this.toggleOptionDropdown(dropdown, false);
          trigger.focus();
        });
      });

      this.optionDropdownOutsideHandler = (event) => {
        if (event.target.closest("[data-pdp-option-select]")) return;
        this.closeOptionDropdowns();
      };
      this.optionDropdownEscapeHandler = (event) => {
        if (event.key !== "Escape") return;
        this.closeOptionDropdowns();
      };

      document.addEventListener("click", this.optionDropdownOutsideHandler);
      document.addEventListener("keydown", this.optionDropdownEscapeHandler);
    }

    toggleOptionDropdown(dropdown, open) {
      const trigger = dropdown.querySelector("[data-pdp-option-select-trigger]");
      const panel = dropdown.querySelector("[data-pdp-option-select-panel]");
      if (!trigger || !panel) return;

      trigger.setAttribute("aria-expanded", open ? "true" : "false");
      panel.hidden = !open;

      if (!open) return;
      const checked = panel.querySelector("[data-pdp-native-value]:checked");
      if (checked) checked.focus({ preventScroll: true });
    }

    closeOptionDropdowns() {
      (this.optionDropdowns || []).forEach((dropdown) => this.toggleOptionDropdown(dropdown, false));
    }

    renderNativeOptions() {
      const variantPrice = this.state.variant?.price == null ? NaN : Number(this.state.variant.price);

      this.nativeOptionElements.forEach((optionElement) => {
        const optionName = optionElement.dataset.optionName || "";
        const optionPosition = Number(optionElement.dataset.optionPosition || 0);
        const selectedValue = this.state.nativeOptions[optionName] || "";
        const optionConfig = this.getOptionConfig(optionName);

        optionElement.querySelectorAll("[data-pdp-native-value]").forEach((input) => {
          const valueConfig = this.findOptionValue(optionName, input.value);
          const checked = input.value === selectedValue;
          input.checked = checked;
          input.disabled = Boolean(valueConfig && valueConfig.available === false && !checked);
          this.updateNativeChoicePrice(input, valueConfig, optionPosition, variantPrice);
        });

        if (!optionConfig && selectedValue) {
          optionElement.dataset.value = selectedValue;
        }

        const selectedLabel = optionElement.querySelector("[data-pdp-native-option-selected]");
        if (selectedLabel) selectedLabel.textContent = selectedValue;

        const dropdownLabel = optionElement.querySelector("[data-pdp-option-select-label]");
        if (dropdownLabel) dropdownLabel.textContent = selectedValue;

        const selectedInput = optionElement.querySelector("[data-pdp-native-value]:checked");
        const selectedIsFabric = selectedInput?.dataset.nativeIsFabric !== "false";
        const selectedFamilySummary = optionElement.querySelector("[data-pdp-native-option-family-summary]");
        if (selectedFamilySummary) selectedFamilySummary.hidden = !selectedIsFabric;

        const selectedValueLabel = optionElement.querySelector("[data-pdp-native-option-value-label]");
        if (selectedValueLabel) {
          const label = selectedIsFabric
            ? selectedValueLabel.dataset.fabricLabel
            : selectedValueLabel.dataset.materialLabel;
          selectedValueLabel.textContent = `${label || ""}: `;
        }

        const selectedFamilyName = optionElement.classList.contains("pdp-fabrics--native")
          ? selectedInput?.dataset.nativeFamilyName || selectedInput?.dataset.nativeFamilyLabel || selectedValue
          : selectedInput?.dataset.nativeFamilyLabel || selectedValue.trim().split(/\s+/)[0] || "";
        optionElement.querySelectorAll("[data-pdp-native-option-family-selected]").forEach((element) => {
          element.textContent = selectedFamilyName;
        });

        const selectedHeadingLabel = optionElement.querySelector("[data-pdp-native-option-heading-label]");
        if (selectedHeadingLabel) {
          const label = selectedIsFabric
            ? selectedHeadingLabel.dataset.fabricLabel
            : selectedHeadingLabel.dataset.materialLabel;
          selectedHeadingLabel.textContent = `${label || ""}:`;
        }

        const selectedFamilyTrigger = optionElement.querySelector("[data-pdp-native-option-family-trigger]");
        if (selectedFamilyTrigger) selectedFamilyTrigger.hidden = !selectedIsFabric;

        const selectedMaterialFamily = optionElement.querySelector("[data-pdp-native-option-material-family]");
        if (selectedMaterialFamily) selectedMaterialFamily.hidden = selectedIsFabric;

        const selectedCategoryLabel = optionElement.querySelector("[data-pdp-native-option-category-selected]");
        if (selectedCategoryLabel) {
          const categoryLabel = selectedIsFabric ? selectedInput?.dataset.nativeCategoryLabel || "" : "";
          selectedCategoryLabel.textContent = categoryLabel;
          selectedCategoryLabel.hidden = !categoryLabel;
        }

        const selectedColorLabel = optionElement.querySelector("[data-pdp-native-option-color-selected]");
        const colorLabel = selectedInput?.dataset.nativeColorLabel || "";
        if (selectedColorLabel) selectedColorLabel.textContent = colorLabel;

        const selectedColorConnector = optionElement.querySelector("[data-pdp-native-option-color-connector]");
        if (selectedColorConnector) selectedColorConnector.hidden = !colorLabel;

        const selectedFabricDetails = optionElement.querySelector("[data-pdp-native-fabric-details]");
        if (selectedFabricDetails) {
          selectedFabricDetails.dataset.handle = selectedInput?.dataset.nativeFabricHandle || "";
        }
      });
    }

    updateNativeChoicePrice(input, valueConfig, optionPosition, variantPrice) {
      const priceElement = input.closest("label")?.querySelector(".product-configurator__choice-price");
      if (!priceElement) return;

      const price = valueConfig?.price == null ? NaN : Number(valueConfig.price);
      let text = "";

      if (Number.isFinite(price)) {
        if (optionPosition === 1) {
          text = this.formatMoneyWithoutTrailingZeros(price);
        } else if (Number.isFinite(variantPrice)) {
          text = this.formatPriceDifference(price - variantPrice);
        }
      }

      priceElement.textContent = text;
      priceElement.hidden = !text;
    }

    renderFabricChoices() {
      const selectedFabric = this.state.fabric;
      const priceGroupOption = this.getOptionConfig(this.config.nativeOptions.priceGroupOptionName);

      this.fabricInputs.forEach((input) => {
        const fabric = this.getFabricChoice(input.value);
        const groupValue = priceGroupOption
          ? this.findOptionValue(priceGroupOption.name, fabric?.priceGroup)
          : null;
        const checked = selectedFabric?.handle === input.value;

        input.checked = checked;
        input.disabled = Boolean(groupValue && groupValue.available === false && !checked);
      });

      if (this.selectors.fabricLabel) {
        this.selectors.fabricLabel.textContent = selectedFabric?.label || "";
      }

      if (this.selectors.fabricFamilyLabel) {
        this.selectors.fabricFamilyLabel.textContent = selectedFabric?.familyName || selectedFabric?.label || "";
      }

      if (this.selectors.fabricCategoryLabel) {
        const categoryLabel = selectedFabric?.isFabric === false ? "" : selectedFabric?.categoryLabel || "";
        this.selectors.fabricCategoryLabel.textContent = categoryLabel;
        this.selectors.fabricCategoryLabel.hidden = !categoryLabel;
      }

      if (this.selectors.fabricColorLabel) {
        this.selectors.fabricColorLabel.textContent = selectedFabric?.colorLabel || "";
      }

      if (this.selectors.fabricColorConnector) {
        this.selectors.fabricColorConnector.hidden = !selectedFabric?.colorLabel;
      }

      document.querySelectorAll("[data-pdp-fabric-type-label]").forEach((element) => {
        element.textContent = selectedFabric?.typeLabel || selectedFabric?.familyName || "";
      });

      document.querySelectorAll("[data-pdp-selected-fabric-details]").forEach((element) => {
        element.dataset.handle = selectedFabric?.handle || "";
      });
    }

    renderSiblingProducts() {
      if (!this.siblingLinks.length) return;
      const currentPath = new URL(this.config.product?.url || window.location.pathname, window.location.origin).pathname;

      this.siblingLinks.forEach((link) => {
        const linkPath = new URL(link.href, window.location.origin).pathname;
        const active = linkPath === currentPath || link.dataset.siblingHandle === this.state.sibling;
        link.classList.toggle("pdp-sibling-product--current", active);
        if (active) {
          link.setAttribute("aria-current", "page");
        } else {
          link.removeAttribute("aria-current");
        }
      });
    }

    renderAddons() {
      this.renderVariantScopedAddons();
      this.renderProductAddonGroups();

      this.addonInputs.forEach((input) => {
        const groupHandle = input.dataset.addonGroupHandle || "";
        const addonGroup = this.getAddonGroup(groupHandle);
        const selectedValue = this.state.addons[groupHandle] || "none";
        const isProductBackedGroup = this.isProductBackedAddonGroup(addonGroup);
        // Editing replaces only the configured lines; standalone add-ons stay
        // independent cart items, so their section is locked with no checked
        // choice — a visible "none" would read as "your mattress is gone" and
        // re-selecting it would add a duplicate line.
        const editLocked = Boolean(this.editConfigKey) && isProductBackedGroup;

        if (input.tagName === "SELECT") {
          input.value = selectedValue;
          Array.from(input.options).forEach((option) => {
            const choice = this.getAddonChoice(groupHandle, option.value);
            option.disabled = editLocked || (this.isResolving && isProductBackedGroup
              ? true
              : !this.isAddonChoiceSelectable(choice, addonGroup));
            this.syncAddonDataset(option, choice);
          });
          return;
        }

        const choice = this.getAddonChoice(groupHandle, input.value);
        input.checked = editLocked ? false : input.value === selectedValue;
        input.disabled = editLocked || (this.isResolving && (isProductBackedGroup || addonGroup?.variantScoped)
          ? true
          : !this.isAddonChoiceSelectable(choice, addonGroup));
        this.syncAddonDataset(input, choice);
      });
    }

    renderVariantScopedAddons() {
      this.config.addonGroups.forEach((group) => {
        const element = this.querySelector(`[data-pdp-addon-group][data-addon-group-handle="${this.escapeAttribute(group.handle)}"]`);
        if (!element) return;
        if (group.variantScoped && group.choicesHtml) {
          if (element.variantChoicesHtml === group.choicesHtml) return;
          if (element.originalChoicesHtml === undefined) element.originalChoicesHtml = element.innerHTML;
          const heading = element.querySelector("legend, .pdp-addon-groups__group-title")?.cloneNode(true);
          element.innerHTML = group.choicesHtml;
          if (element.classList.contains("pdp-addon-groups__group")) {
            const inlineClasses = {
              "product-configurator__addon-grid": "pdp-addon-groups__list",
              "product-configurator__addon-card": "pdp-addon-group",
              "product-configurator__addon-media": "pdp-addon-group__image",
              "product-configurator__addon-content": "pdp-addon-group__content",
              "product-configurator__addon-head": "pdp-addon-group__header",
            };
            Object.entries(inlineClasses).forEach(([drawerClass, inlineClass]) => {
              element.querySelectorAll(`.${drawerClass}`).forEach((node) => node.classList.replace(drawerClass, inlineClass));
            });
            element.querySelectorAll(".product-configurator__choice-check").forEach((node) => node.remove());
          }
          if (heading) element.prepend(heading);
          element.querySelectorAll("[data-pdp-addon-choice]").forEach((input) => {
            input.name = `${this.instanceId}-addon-${group.handle}`;
          });
          element.variantChoicesHtml = group.choicesHtml;
        } else if (element.variantChoicesHtml !== undefined) {
          element.innerHTML = element.originalChoicesHtml;
          delete element.variantChoicesHtml;
        }
      });
      this.addonInputs = Array.from(this.querySelectorAll("[data-pdp-addon-choice]"));
    }

    renderProductAddonGroups() {
      this.config.addonGroups.forEach((addonGroup) => {
        if (!this.isProductBackedAddonGroup(addonGroup)) return;

        const groupSelector = `[data-pdp-addon-group][data-addon-group-handle="${this.escapeAttribute(addonGroup.handle)}"]`;
        const groupElement = this.querySelector(groupSelector);
        if (!groupElement) return;

        const editNotice = groupElement.querySelector("[data-product-addon-edit-notice]") ||
          groupElement.closest("[data-configurator-step]")?.querySelector("[data-product-addon-edit-notice]");
        if (editNotice) editNotice.hidden = !this.editConfigKey;

        const matchContext = groupElement.querySelector("[data-product-addon-match-context]") ||
          groupElement.closest("[data-configurator-step]")?.querySelector("[data-product-addon-match-context]");
        const matchValue = matchContext?.querySelector("[data-product-addon-match-value]");
        if (matchContext) {
          const label = addonGroup.matchContext?.label || "";
          const value = addonGroup.matchContext?.matchValue || "";
          matchContext.hidden = !label && !value;
          const labelElement = matchContext.querySelector("span");
          if (labelElement) labelElement.textContent = label;
          if (matchValue) matchValue.textContent = value;
        }

        groupElement.querySelectorAll("[data-product-addon-card]").forEach((card) => {
          const choiceValue = card.dataset.addonChoiceValue || "";
          const choice = this.getAddonChoice(addonGroup.handle, choiceValue);
          this.renderProductAddonCard(card, choice, addonGroup);
        });
      });
    }

    renderProductAddonCard(card, choice, addonGroup) {
      const status = choice?.resolution?.status || "invalid_config";
      const selectable = this.isAddonChoiceSelectable(choice, addonGroup);
      const visible = status === "resolved" || status === "unavailable" || Boolean(window.Shopify?.designMode);
      const input = card.querySelector("[data-pdp-addon-choice]");
      const editLocked = Boolean(this.editConfigKey);

      card.hidden = !visible;
      card.dataset.resolutionStatus = status;
      card.classList.toggle("is-unavailable", visible && (!selectable || editLocked));
      card.classList.toggle("is-resolved", status === "resolved");

      if (input) {
        input.disabled = editLocked || this.isResolving || !selectable;
        this.syncAddonDataset(input, choice);
      }

      const title = card.querySelector("[data-product-addon-title]");
      if (title && choice?.label) title.textContent = choice.label;

      const price = card.querySelector("[data-product-addon-price]");
      if (price) price.textContent = this.formatPriceDifference(choice?.price || 0);

      const variant = card.querySelector("[data-product-addon-variant]");
      if (variant) {
        const variantText = this.getAddonVariantSummary(choice);
        variant.textContent = variantText;
        variant.hidden = !variantText;
      }

      const link = card.querySelector("[data-product-addon-link]");
      const url = choice?.resolvedVariant?.url || choice?.product?.url || "";
      if (link) {
        if (url) link.setAttribute("href", url);
        link.hidden = !url || !visible;
      }

      const media = card.querySelector("[data-product-addon-media]");
      const image = card.querySelector("[data-product-addon-image]");
      if (image) {
        if (choice?.image) {
          if (image.getAttribute("src") !== choice.image) image.setAttribute("src", choice.image);
          image.setAttribute("alt", choice.label || "");
          image.hidden = false;
          if (media) media.hidden = false;
        } else {
          image.removeAttribute("src");
          image.hidden = true;
          if (media) media.hidden = true;
        }
      }

      const diagnostic = card.querySelector("[data-product-addon-diagnostic]");
      if (diagnostic) {
        const message = this.getAddonResolutionMessage(choice);
        diagnostic.textContent = message;
        diagnostic.hidden = !message || status === "resolved";
      }

      this.warnAddonResolution(addonGroup, choice);
    }

    renderPrice() {
      const selectedAddons = this.getSelectedAddons();
      document.querySelectorAll("[data-pdp-configuration-description]").forEach((element) => {
        if (element.dataset.productId !== this.selectors.product?.value) return;
        const parts = [element.dataset.productType, ...selectedAddons.map((addon) => addon.label)]
          .map((value) => String(value || "").trim())
          .filter(Boolean);
        element.textContent = parts.join(" | ");
        element.hidden = parts.length === 0;
      });

      const variant = this.state.variant;
      this.configurationPrice = null;
      if (!variant) return;

      const addonsTotal = selectedAddons.reduce((sum, addon) => {
        return sum + Number(addon.priceContribution ?? addon.price ?? 0);
      }, 0);
      const total = Number(variant.price || 0) + addonsTotal;
      this.configurationPrice = total;
      const formatted = this.formatMoney(total);

      document.querySelectorAll("[data-pdp-price]").forEach((element) => {
        element.textContent = formatted;
        element.dataset.pdpPriceCents = String(total);
      });

      if (this.selectors.footerPrice) {
        this.selectors.footerPrice.textContent = formatted;
      }

      if (this.selectors.footer) {
        this.selectors.footer.style.display = "";
      }
    }

    renderVariantDetails() {
      const variantId = String(this.state.variant?.id || "");
      const fabricHandle = this.mode === "upholstery_hybrid" ? this.state.fabric?.handle || "" : "";
      const hasShippingOverride = Boolean(fabricHandle) && Array.from(document.querySelectorAll("[data-shipping-fabric]"))
        .some((element) => element.dataset.shippingFabric === fabricHandle && element.dataset.variantDetail === variantId);

      if (this.selectors.variant) {
        this.selectors.variant.value = variantId;
      }

      document.querySelectorAll("[data-variant-detail]").forEach((element) => {
        const ids = (element.dataset.variantDetail || "").split(",");
        let selected = ids.includes(variantId);
        if (element.hasAttribute("data-shipping-fabric")) {
          const shippingFabric = element.dataset.shippingFabric;
          selected = selected && (shippingFabric ? shippingFabric === fabricHandle : !hasShippingOverride);
        }
        element.classList.toggle("selected", selected);
      });

      document.querySelectorAll("[data-pdp-native-fabric-information]").forEach((element) => {
        element.hidden = !element.querySelector("[data-variant-detail].selected");
      });

      Object.entries(this.getVisibleSelectedOptions()).forEach(([name, value]) => {
        document.querySelectorAll(`.richtext [data-var="${this.escapeAttribute(name)}"]`).forEach((element) => {
          element.textContent = value;
        });
      });
    }

    renderTechnicalParameterImages() {
      document.querySelectorAll("[data-pdp-technical-images]").forEach((list) => {
        if (list.dataset.pdpTechnicalImages !== this.getProductHandle()) return;

        const elements = Array.from(list.querySelectorAll("[data-pdp-technical-image]"));
        const mediaItems = elements.map((element) => ({ fileBase: element.dataset.pdpTechnicalImage }));
        const groups = this.buildGalleryGroups(mediaItems);
        const matches = mediaItems.map((media) => this.mediaMatchesGalleryGroups(media, groups));
        const hasAssignedMatch = mediaItems.some((media, index) => matches[index] && groups.some((group) => {
          return group.activeTokens.some((token) => this.mediaContainsGalleryToken(media, token));
        }));

        // Keep the last visible dimensions when the selected fabric has no drawing.
        if (groups.length && !hasAssignedMatch && elements.some((element) => !element.hidden)) return;

        elements.forEach((element, index) => {
          element.hidden = !matches[index];
        });
        list.hidden = elements.every((element) => element.hidden);
      });
    }

    renderFabricDetails() {
      const fabricHandle = this.state.fabric?.handle || "";

      if (!fabricHandle) return;

      document.querySelectorAll("[data-pdp-fabric-detail]").forEach((element) => {
        const handles = (element.dataset.pdpFabricDetail || "").split(",");
        element.classList.toggle("selected", handles.includes(fabricHandle));
      });
    }

    renderGallery() {
      const gallery = this.resolveGallery();

      this.warnGallery(gallery);
      this.dispatchConfigurationChange(gallery);
    }

    dispatchConfigurationChange(gallery = null, options = {}) {
      if (this.isApplyingExternalState) return;

      const detail = this.getConfigurationDetail(gallery || this.resolveGallery());
      if (options.variantChange !== false) {
        window.dispatchEvent(new CustomEvent("variantChange", { detail }));
      }
      window.dispatchEvent(new CustomEvent("pdpConfigurationChange", { detail }));
    }

    getConfigurationDetail(gallery) {
      const fabric = this.state.fabric;
      const fabricMismatch = this.hasGalleryFabricMismatch(gallery);
      return {
        sourceId: this.instanceId,
        product: this.getProductHandle(),
        config: this.config,
        variant: String(this.state.variant?.id || ""),
        variantId: String(this.state.variant?.id || ""),
        variantObject: this.state.variant || null,
        nativeOptions: Object.assign({}, this.state.nativeOptions || {}),
        optionValueIds: Array.isArray(this.state.optionValueIds) ? [...this.state.optionValueIds] : [],
        addons: Object.assign({}, this.state.addons || {}),
        droppedAddons: Object.assign({}, this.droppedAddons || {}),
        sibling: this.state.sibling || "",
        fabricHandle: fabric?.handle || "",
        familyHandle: fabric?.familyHandle || "",
        optionTokens: this.getSelectedOptionTokens(),
        gallery: {
          ...gallery,
          fabricMismatch,
          materialMismatch: fabricMismatch && this.hasSelectedNonFabricOption(),
        },
      };
    }

    hasGalleryFabricMismatch(gallery) {
      if (!gallery?.mediaIndexes?.length) return false;

      // Check the current selection, not the groups saved with the previous gallery.
      const fabricGroups = this.mode === "upholstery_hybrid"
        ? (this.state.fabric ? [{ activeTokens: this.buildGalleryGroups().find((group) => group.code === "fabric")?.activeTokens || [] }] : [])
        : this.optionConfigs().flatMap((option) => {
          const selectedValue = this.state.nativeOptions[option.name] || option.selectedValue || "";
          const value = this.findOptionValue(option.name, selectedValue);
          return value?.linkedFabric
            ? [{ activeTokens: this.getNativeOptionValueGalleryTokens(value, option.name) }]
            : [];
        });

      return fabricGroups.some((group) => !gallery.mediaIndexes.some((index) => {
        const media = this.getMediaByIndex(index);
        return group.activeTokens.some((token) => this.mediaContainsGalleryToken(media, token));
      }));
    }

    hasSelectedNonFabricOption() {
      return this.mode === "native" && (this.config.nativeOptions?.options || []).some((option) => {
        const selectedValue = this.state.nativeOptions[option.name] || option.selectedValue || "";
        return this.findOptionValue(option.name, selectedValue)?.linkedFabric?.isFabric === false;
      });
    }

    handleExternalConfigurationChange(event) {
      const detail = event.detail || {};
      if (!detail || detail.sourceId === this.instanceId) return;
      if (detail.product && detail.product !== this.getProductHandle()) return;
      if (this.externalConfigurationMatches(detail)) return;

      this.applyExternalConfiguration(detail);
    }

    externalConfigurationMatches(detail) {
      const variantMatches = String(this.state.variant?.id || "") === String(detail.variantId || detail.variant || "");
      const fabricMatches = String(this.state.fabric?.handle || "") === String(detail.fabricHandle || "");
      const optionsMatch = this.shallowObjectEqual(this.state.nativeOptions || {}, detail.nativeOptions || {});
      const addonsMatch = this.shallowObjectEqual(this.state.addons || {}, detail.addons || {});
      const optionValueIdsMatch = this.arrayEqual(
        (this.state.optionValueIds || []).map((id) => String(id)),
        (detail.optionValueIds || []).map((id) => String(id)),
      );

      return variantMatches && fabricMatches && optionsMatch && addonsMatch && optionValueIdsMatch;
    }

    applyExternalConfiguration(detail) {
      this.isApplyingExternalState = true;

      try {
        if (detail.config) {
          this.config = detail.config;
        }

        if (detail.nativeOptions && typeof detail.nativeOptions === "object") {
          this.state.nativeOptions = Object.assign({}, detail.nativeOptions);
        }

        this.state.optionValueIds = Array.isArray(detail.optionValueIds)
          ? detail.optionValueIds.map((id) => this.normalizeId(id)).filter(Boolean)
          : [];

        this.state.variant = detail.variantObject || null;

        if (detail.fabricHandle) {
          this.state.fabric = this.getFabricChoice(detail.fabricHandle) || this.state.fabric;
          if (this.state.fabric) {
            this.applyFabricToNativeState(this.state.fabric, this.state.nativeOptions);
          }
        } else {
          this.state.fabric = null;
        }

        if (detail.addons && typeof detail.addons === "object") {
          const nextAddons = {};
          this.config.addonGroups.forEach((addonGroup) => {
            const selectedValue = detail.addons[addonGroup.handle] || "none";
            nextAddons[addonGroup.handle] = this.resolveAddonStateValue(addonGroup, selectedValue);
          });
          this.state.addons = nextAddons;
        }

        if (detail.droppedAddons && typeof detail.droppedAddons === "object") {
          this.droppedAddons = Object.assign({}, detail.droppedAddons);
        }

        this.state.sibling = detail.sibling || "";
        this.renderAll();
      } finally {
        this.isApplyingExternalState = false;
      }
    }

    renderStatus() {
      const addonValidation = this.getAddonValidationState();
      let message = "";
      let nonBlocking = false;
      let announceValidation = false;

      if (this.isResolving) {
        message = this.selectors.status?.dataset.resolving || "";
      } else if (this.resolveError) {
        message = this.selectors.status?.dataset.resolutionError || this.selectors.status?.dataset.unavailable || "";
        nonBlocking = true;
      } else if (this.mode === "upholstery_hybrid" && !this.state.fabric) {
        message = this.selectors.status?.dataset.chooseFabric || window.i18?.required || "";
      } else if (!this.state.variant || this.state.variant.available === false) {
        message = this.selectors.status?.dataset.unavailable || "";
      } else if (addonValidation.message) {
        message = addonValidation.message;
        announceValidation = addonValidation.announce === true;
      } else if (this.cartError) {
        message = this.cartError;
        nonBlocking = true;
      } else if (this.addonRemovedNotice) {
        message = this.addonRemovedNotice;
        nonBlocking = true;
      }

      if (this.selectors.status) {
        this.selectors.status.textContent = message;
        this.selectors.status.hidden = !message;
      }

      // Skip the tracker while pending so an unchanged blocking state is not
      // re-announced after every resolve round-trip.
      if (!this.isResolving) {
        this.announceAddonValidation(announceValidation ? message : "");
      }

      // Non-blocking states keep the button clickable: a retryable failure
      // re-runs its step on click, an informational notice never gates submit.
      this.submitButtons.forEach((button) => {
        button.disabled = Boolean(this.isResolving || (message && !nonBlocking));
      });

      this.renderSummaryTitle(Boolean(message && !nonBlocking));
      this.renderSubmitLabels();
      this.publishPrice(addonValidation);
    }

    publishPrice(addonValidation) {
      if (this.isApplyingExternalState) return;

      const productId = String(this.config.product.id);
      const schema = document.querySelector(`[data-pdp-structured-data][data-product-id="${productId}"]`);
      if (!schema) return;

      const pending = Array.from(document.querySelectorAll("product-configurator")).some((form) => {
        return String(form.config?.product?.id) === productId && form.isResolving;
      });
      const variant = this.state.variant;
      const amount = this.configurationPrice;
      const missingRequiredAddon = this.config.addonGroups.some((group) => {
        if (!group.required || (this.editConfigKey && this.isProductBackedAddonGroup(group))) return false;
        const value = this.state.addons[group.handle];
        return !value || value === "none";
      });
      const valid = !pending && !this.resolveError && Boolean(variant) &&
        Number.isSafeInteger(amount) && amount >= 0 && !addonValidation.message && !missingRequiredAddon &&
        (this.mode !== "upholstery_hybrid" || Boolean(this.state.fabric));
      const ready = valid && variant.available !== false && amount > 0;

      try {
        const data = JSON.parse(schema.textContent);
        if (valid) {
          const url = this.getConfigurationUrl(new URL(this.config.product.url, data.url).href).toString();
          data.offers = {
            "@type": "Offer",
            price: amount / 100,
            priceCurrency: schema.dataset.currency,
            availability: `https://schema.org/${variant.available === false ? "OutOfStock" : "InStock"}`,
            url,
          };
          data.url = url;
          if (variant.sku) {
            data.sku = variant.sku;
            data.offers.sku = variant.sku;
          } else {
            delete data.sku;
          }
        } else {
          delete data.offers;
        }
        const json = JSON.stringify(data);
        if (schema.textContent !== json) schema.textContent = json;
      } catch (error) {
        console.error("[ProductConfigurator] Invalid product structured data", error);
      }

      schema.dataset.pdpPriceCents = String(amount ?? "");
      schema.dataset.pdpPriceReady = String(ready);
      window.dispatchEvent(new CustomEvent("pdpPriceChange", { detail: { productId, amount, ready } }));
    }

    // The summary panel used to cheer "your product is ready" regardless of
    // validation; while submission is blocked it asks to finish instead.
    // Pending states keep the previous title to avoid flicker per resolve.
    renderSummaryTitle(blocked) {
      if (this.isResolving) return;

      const title = document.querySelector("[data-summary-state-title]");
      if (!title || !title.dataset.blockedLabel) return;
      if (!title.dataset.readyLabel) title.dataset.readyLabel = title.textContent.trim();
      title.textContent = blocked ? title.dataset.blockedLabel : title.dataset.readyLabel;
    }

    // Edit mode replaces the configured lines instead of adding new ones, so
    // the submit reads "confirm changes" until the edit completes.
    renderSubmitLabels() {
      const editMode = Boolean(this.editConfigKey);

      this.submitButtons.forEach((button) => {
        const label = button.querySelector("[data-submit-label]");
        const editLabel = button.dataset.editLabel || "";
        if (!label || !editLabel) return;
        if (!label.dataset.defaultLabel) label.dataset.defaultLabel = label.textContent.trim();
        label.textContent = editMode ? editLabel : label.dataset.defaultLabel;
      });
    }

    updateUrl(options = {}) {
      if (this.isHandlingPopState) return;

      const url = this.getConfigurationUrl();
      const historyState = {
        configurator: true,
        variant: this.state.variant?.id || "",
        option_values: this.state.optionValueIds,
      };
      const method = options.replace ? "replaceState" : "pushState";
      history[method](historyState, "", url.toString());
    }

    getConfigurationUrl(base = window.location.href) {
      const url = new URL(base);
      url.searchParams.delete("section_id");

      if (this.state.variant?.id) {
        url.searchParams.set("variant", this.state.variant.id);
      } else {
        url.searchParams.delete("variant");
      }

      if (this.state.optionValueIds.length) {
        url.searchParams.set("option_values", this.state.optionValueIds.join(","));
      } else {
        url.searchParams.delete("option_values");
      }

      if (this.state.fabric?.handle) {
        url.searchParams.set("fabric", this.state.fabric.handle);
      } else {
        url.searchParams.delete("fabric");
      }

      Array.from(url.searchParams.keys()).forEach((key) => {
        if (key.startsWith("ao_")) url.searchParams.delete(key);
      });

      this.config.addonGroups.forEach((addonGroup) => {
        url.searchParams.set(`ao_${addonGroup.handle}`, this.state.addons[addonGroup.handle] || "none");
      });

      url.searchParams.delete("sibling");
      url.searchParams.delete("sibling_product");

      return url;
    }

    async addToCart() {
      if (this.resolveError && !this.isResolving) {
        // The click carries purchase intent: retry the resolve and continue
        // into the add once it succeeds instead of demanding a second click.
        // On another failure the retryable status stays and nothing loops.
        this.resolveVariant({ updateHistory: false }).then((variant) => {
          if (variant && !this.resolveError) this.addToCart();
        });
        return;
      }

      const addonValidationMessage = this.getAddonValidationMessage();

      if (
        !this.state.variant ||
        this.state.variant.available === false ||
        this.isResolving ||
        this.editSubmitting ||
        addonValidationMessage ||
        this.submitButtons.some((button) => button.disabled)
      ) {
        this.renderStatus();
        return;
      }

      this.cartError = "";
      let editSource = null;
      if (this.editConfigKey) {
        this.setEditSubmitting(true);
        try {
          // This is a business preflight, not an analytics dependency: an old
          // link must never replace another configuration with the same bed.
          editSource = await this.loadEditOfferContext();
          if (!this.state.variant || this.state.variant.available === false || this.isResolving ||
              this.resolveError || this.getAddonValidationMessage()) {
            this.setEditSubmitting(false);
            this.renderStatus();
            return;
          }
        } catch (_) {
          this.cartError = this.selectors.status?.dataset.cartError || this.selectors.status?.dataset.unavailable || "";
          this.announceCartError(this.cartError);
          this.setEditSubmitting(false);
          return;
        }
      }
      const analytics = window.IWCAnalytics;
      let offerSelection = null;
      let baseOfferProperties = {};
      let addonOfferProperties = {};
      try {
        offerSelection = analytics?.fromConfigurator(this) || null;
        baseOfferProperties = analytics?.properties(offerSelection, "base") || {};
        addonOfferProperties = analytics?.properties(offerSelection, "addon") || {};
      } catch (_) {
        offerSelection = null;
        baseOfferProperties = {};
        addonOfferProperties = {};
        console.info("[IWC analytics] configurator_context_unavailable");
      }
      const selectedAddons = this.getSelectedAddons().filter((addon) =>
        !this.editConfigKey || addon.cartLineMode !== "standalone_product");
      const configKey = this.getConfigKey();
      const properties = Object.assign(this.getBaseProperties(configKey), baseOfferProperties);
      const quantity = this.getQuantity();
      const items = [
        {
          id: String(this.state.variant.id),
          quantity,
          properties,
        },
      ];

      selectedAddons.forEach((addon) => {
        if (addon.cartLineMode === "standalone_product") {
          items.push({
            id: String(addon.variantId),
            quantity,
          });
          return;
        }

        items.push({
          id: String(addon.variantId),
          quantity,
          properties: {
            ...addonOfferProperties,
            _config_key: configKey,
            _parent_variant_id: String(this.state.variant.id),
            _parent_product_handle: this.config.product?.handle || this.selectors.handle?.value || "",
            _addon_group: addon.groupHandle,
            _addon_code: addon.value,
            _addon_label: addon.propertyLabel || addon.label || addon.groupLabel || addon.groupHandle,
          },
        });
      });

      this.setSubmitting(true);

      const operationScope = {
        items,
        variants: items.map((item) => item.id),
        editConfigKey: this.editConfigKey || "",
        editIwcKey: this.editIwcKey || "",
        editLineKey: this.editLineKey || "",
        keys: editSource?.items.map((item) => item.key),
      };
      const addConfiguration = async (beforeCart) => {
        if (editSource) {
          // Use the state at the operation boundary if another queued cart
          // mutation finished after the initial source/context preflight.
          editSource = this.getEditSource(beforeCart || await this.readEditCart());
          operationScope.keys = editSource.items.map((item) => item.key);
        }
        return this.fetchWithTimeout("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      })
        .then((response) => {
          if (!response.ok) {
            return response.text().then((body) => {
              let message = "";
              try {
                const data = JSON.parse(body);
                message = data.description || data.message || "";
              } catch (_) {
                // Shopify returned HTML; fall through to the generic cart error.
              }
              const cartAddError = new Error(message || `Cart add failed (HTTP ${response.status})`);
              // Only a parsed Shopify description is customer-readable; network
              // failures and timeouts get the localized generic message below.
              cartAddError.isCartResponseError = Boolean(message);
              throw cartAddError;
            });
          }
          return response.json();
        })
        .then((response) => {
          if (!response.items) return;
          operationScope.addedItems = response.items;
          return this.replaceEditedConfiguration(response.items, quantity, editSource).finally(() => {
            window.dispatchEvent(new CustomEvent("cartAdded"));
            window.dispatchEvent(new CustomEvent("cartCounterUpdate"));
          });
        });
      };

      // One completed operation includes every linked line and edit cleanup.
      // cartAdded also fires when cleanup fails, so it is not the success signal.
      const operation = typeof analytics?.runCartOperation === "function"
        ? analytics.runCartOperation(addConfiguration, offerSelection, operationScope)
        : addConfiguration();
      return operation
        .catch((error) => {
          console.error("[ProductConfigurator] Add to cart failed", error);
          this.cartError = (error?.isCartResponseError && error.message) ||
            this.selectors.status?.dataset.cartError ||
            this.selectors.status?.dataset.unavailable || "";
          this.announceCartError(this.cartError);
          this.setSubmitting(false);
        })
        .finally(() => {
          if (editSource) this.setEditSubmitting(false);
          setTimeout(() => this.setSubmitting(false), 1000);
        });
    }

    getEditPeers() {
      const productId = String(this.config.product?.id || "");
      const peers = Array.from(document.querySelectorAll?.("product-configurator") || []).filter((peer) =>
        String(peer.config?.product?.id || "") === productId);
      return peers.includes(this) ? peers : [...peers, this];
    }

    setEditSubmitting(submitting) {
      this.getEditPeers().forEach((peer) => {
        peer.editSubmitting = submitting;
        peer.setSubmitting(submitting);
      });
    }

    async readEditCart() {
      const response = await this.fetchWithTimeout("/cart.js");
      if (!response.ok) throw new Error("edit_cart_unavailable");
      return typeof window.IWCAnalytics?.parseCartJSON === "function"
        ? window.IWCAnalytics.parseCartJSON(await response.text()) : response.json();
    }

    getEditSource(cart) {
      const items = (cart.items || []).filter((item) =>
        item.properties?._config_key === this.editConfigKey &&
        (item.properties?._iwc_configuration_key || "") === (this.editIwcKey || ""));
      const bases = items.filter((item) => !["_addon_group", "_addon_group_handle", "_parent_variant_id", "_parent_variant", "_parent_bed_variant"]
        .some((key) => item.properties?.[key]));
      // A missing line key may be a Shopify re-key after discounts; only one
      // base in the exact old/new identity pair is a safe replacement target.
      if (bases.length !== 1 || String(bases[0].product_id) !== String(this.config.product?.id)) {
        throw new Error("edit_configuration_missing_or_ambiguous");
      }
      const base = bases[0];
      if (this.editLineKey && cart.items.some((item) => item.key === this.editLineKey) && base.key !== this.editLineKey) {
        throw new Error("edit_configuration_mismatch");
      }
      return { items, base, configKey: this.editConfigKey, iwcKey: this.editIwcKey || "" };
    }

    async loadEditOfferContext() {
      const editKey = this.editConfigKey;
      const iwcKey = this.editIwcKey;
      const token = this.editContextLoadToken = (this.editContextLoadToken || 0) + 1;
      this.editContextLoading = true;
      this.editOfferContext = null;
      try {
        const cart = await this.readEditCart();
        if (token !== this.editContextLoadToken || !editKey || editKey !== this.editConfigKey || iwcKey !== this.editIwcKey) throw new Error("edit_expired");
        const source = this.getEditSource(cart);
        let context = null;
        try { context = JSON.parse(source.base.properties?._iwc_offer_context || "null"); } catch (_) { /* Legacy or invalid context stays unresolved. */ }
        const analytics = window.IWCAnalytics;
        if (context && analytics?.normalizeContext) {
          context = analytics.normalizeContext(context);
          if (context && (analytics.configurationKey(context) !== source.iwcKey ||
              context.product_id !== String(source.base.product_id) ||
              context.selection.variant_id !== String(source.base.variant_id))) context = null;
        }
        if (source.iwcKey && !context) throw new Error("edit_context_invalid");
        this.editOfferContext = context;
        return source;
      } finally {
        if (token === this.editContextLoadToken) this.editContextLoading = false;
      }
    }

    clearEditState() {
      this.getEditPeers().forEach((peer) => {
        peer.editConfigKey = "";
        peer.editIwcKey = "";
        peer.editLineKey = "";
        peer.editOfferContext = null;
        peer.editContextLoading = false;
        peer.renderAddons();
        peer.renderPrice?.();
        peer.renderStatus();
      });
      const url = new URL(window.location.href);
      ["edit_config", "edit_qty", "edit_iwc_key", "edit_line"].forEach((key) => url.searchParams.delete(key));
      history.replaceState(history.state, "", url.toString());
    }

    // Edit flow: after the re-added lines are in the cart, drop the previous
    // lines of the edited configuration and normalize a merged quantity when
    // nothing changed. Standalone product-backed lines carry no _config_key
    // and stay untouched. On cleanup failure both configurations stay visible
    // in the refreshed cart, so the customer can resolve it manually.
    replaceEditedConfiguration(addedItems, quantity, source) {
      const configKey = this.editConfigKey;
      if (!configKey) return Promise.resolve();
      if (!source) return Promise.reject(new Error("edit_source_missing"));

      return this.readEditCart()
        .then((cart) => {
          const updates = {};
          const identity = (item) => JSON.stringify([String(item.variant_id || item.id),
            Object.entries(item.properties || {}).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0),
            String(item.selling_plan_allocation?.selling_plan?.id || "")]);
          const remaining = new Map();
          source.items.forEach((item) => remaining.set(identity(item), (remaining.get(identity(item)) || 0) + item.quantity));
          (cart.items || []).forEach((item) => {
            if (item.properties?._config_key !== source.configKey ||
                (item.properties?._iwc_configuration_key || "") !== source.iwcKey) return;
            const key = identity(item);
            const removed = Math.min(item.quantity, remaining.get(key) || 0);
            if (!removed) return;
            // Subtract only the captured source quantity, even if Shopify has
            // re-keyed/split it after a discount or merged the re-add. A different
            // destination keeps its pre-existing quantity plus the actual add.
            updates[item.key] = item.quantity - removed;
            remaining.set(key, remaining.get(key) - removed);
          });
          if (!Object.keys(updates).length) return null;

          return this.fetchWithTimeout("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ updates }),
          }).then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
          });
        })
        .catch((error) => {
          console.error("[ProductConfigurator] Replacing edited configuration failed", error);
        }).finally(() => this.clearEditState());
    }

    handlePopState() {
      if (window.__drawerHandledPopstate) return;

      clearTimeout(this.resolveTimer);
      const urlState = this.parseUrlState();
      this.isHandlingPopState = true;

      if (urlState.optionValueIds.length) {
        this.applyOptionValueIdsToNativeState(urlState.optionValueIds, this.state.nativeOptions);
        this.state.optionValueIds = urlState.optionValueIds;
        this.state.fabric = this.resolveInitialFabric(urlState, this.state);
        if (this.state.fabric) {
          this.applyFabricToNativeState(this.state.fabric, this.state.nativeOptions);
        }
        this.applyRawAddonUrlState(urlState);
        // Sync the radios to the restored state before resolving:
        // getNativeSelectedOptions() overrides state with DOM-checked values,
        // so stale checks would resolve the outgoing config again.
        this.renderNativeOptions();
        this.resolveVariant({ updateHistory: false }).finally(() => {
          this.isHandlingPopState = false;
        });
        return;
      }

      if (urlState.variantId) {
        const token = ++this.resolveToken;
        this.resolveError = false;
        this.setResolving(true);
        this.fetchSectionConfig({ variantId: urlState.variantId })
          .then((config) => {
            if (token !== this.resolveToken) return;
            this.config = config;
            this.state.nativeOptions = this.getInitialNativeOptions();
            this.state.variant = config.currentVariant || null;
            this.state.fabric = this.resolveInitialFabric(urlState, this.state);
            if (this.state.fabric) {
              this.applyFabricToNativeState(this.state.fabric, this.state.nativeOptions);
            }
            this.applyAddonUrlState(urlState);
            this.state.optionValueIds = this.getSelectedOptionValueIds(this.adapter.getSelectedOptions());
            this.renderAll();
          })
          .catch((error) => {
            if (token !== this.resolveToken) return;
            this.resolveError = true;
            // State and visible options stay mutually consistent; only the URL
            // is ahead until the next interaction re-resolves.
            console.error("[ProductConfigurator] History restore failed", error);
          })
          .finally(() => {
            if (token === this.resolveToken) {
              this.isHandlingPopState = false;
              this.setResolving(false);
            }
          });
        return;
      }

      this.state.nativeOptions = this.getInitialNativeOptions();
      this.state.fabric = this.resolveInitialFabric(urlState, this.state);
      if (this.state.fabric) {
        this.applyFabricToNativeState(this.state.fabric, this.state.nativeOptions);
      }
      this.applyAddonUrlState(urlState);
      this.state.optionValueIds = this.getSelectedOptionValueIds(this.adapter.getSelectedOptions());
      this.renderAll();
      this.isHandlingPopState = false;
      this.publishOfferView();
    }

    applyAddonUrlState(urlState) {
      this.state.addons = {};
      this.config.addonGroups.forEach((addonGroup) => {
        const selectedValue = urlState.addons[addonGroup.handle] || "none";
        this.state.addons[addonGroup.handle] = this.resolveAddonStateValue(addonGroup, selectedValue);
      });
      this.state.sibling = urlState.sibling;
    }

    // History restore before a section fetch (docs 13.3): store the exact ao_*
    // values and let reconcileAddonState validate them against the config
    // fetched for the restored options. Validating here against the outgoing
    // config would collapse a choice that only resolves in the restored one
    // (review finding A3).
    applyRawAddonUrlState(urlState) {
      this.state.addons = {};
      this.config.addonGroups.forEach((addonGroup) => {
        this.state.addons[addonGroup.handle] = urlState.addons[addonGroup.handle] || "none";
      });
      this.state.sibling = urlState.sibling;
    }

    handleSiblingClick(event, link) {
      if (link.getAttribute("aria-current") === "page") {
        event.preventDefault();
      }
    }

    getSelectedOptionValueIds(selectedOptions) {
      return this.optionConfigs()
        .map((option) => {
          const selectedValue = selectedOptions[option.name] || option.selectedValue || option.values?.[0]?.name || "";
          const valueConfig = this.findOptionValue(option.name, selectedValue);
          return this.normalizeId(valueConfig?.id || this.config.nativeOptions.optionValueIds?.[option.name]?.[selectedValue]);
        })
        .filter(Boolean);
    }

    getNativeSelectedOptions() {
      const selected = Object.assign({}, this.state.nativeOptions);

      this.nativeOptionElements.forEach((option) => {
        const name = option.dataset.optionName || "";
        const checked = option.querySelector("[data-pdp-native-value]:checked");
        if (checked) selected[name] = checked.value;
      });

      return selected;
    }

    getVisibleSelectedOptions() {
      const selected = Object.assign({}, this.state.nativeOptions);
      const priceGroupName = this.config.nativeOptions.priceGroupOptionName;

      if (this.mode === "upholstery_hybrid" && priceGroupName) {
        delete selected[priceGroupName];
      }

      return selected;
    }

    getSelectedOptionTokens() {
      return this.nativeOptionElements.flatMap((option) => {
        const checked = option.querySelector("[data-pdp-native-value]:checked");
        return this.parseTokens(checked?.dataset.galleryTokens || "");
      });
    }

    resolveGallery() {
      const defaultIndexes = this.getDefaultMediaIndexes();

      if (!defaultIndexes.length) {
        return {
          source: "empty",
          mediaIndexes: [],
          warning: "Product has no gallery media.",
        };
      }

      const defaultGallery = (source = "default", warning = "") => ({
        source,
        mediaIndexes: defaultIndexes,
        warning,
      });

      const galleryGroups = this.buildGalleryGroups();
      const fabricGroup = this.mode === "upholstery_hybrid"
        ? galleryGroups.find((group) => group.code === "fabric")
        : null;
      const materialGroup = fabricGroup || galleryGroups.find((group) => group.isNonFabricMaterial);
      const hasMaterialMedia = materialGroup && (this.config.media?.items || []).some((media) => {
        return materialGroup.activeTokens.some((token) => this.mediaContainsGalleryToken(media, token));
      });
      const rememberedGallery = this.mode === "upholstery_hybrid"
        ? this.lastFabricGallery : this.lastMaterialGallery;

      // Keep the last illustrated material while browsing choices without media.
      // Neutral images alone must not replace that remembered gallery.
      if ((materialGroup || (fabricGroup == null && this.mode === "upholstery_hybrid" && this.state.fabric)) &&
        !hasMaterialMedia && rememberedGallery) {
        return rememberedGallery;
      }

      if (galleryGroups.length) {
        const groupedIndexes = this.filterMediaIndexesByGroups(defaultIndexes, galleryGroups);
        // A direct link can start on an unillustrated material, before anything
        // has been remembered. Seed one coherent gallery, not every material.
        if (!groupedIndexes.length && materialGroup && !hasMaterialMedia) {
          const otherGroups = galleryGroups.filter((group) => group !== materialGroup);
          const fallbackIndex = this.filterMediaIndexesByGroups(defaultIndexes, otherGroups).find((index) => {
            const media = this.getMediaByIndex(index);
            return (!media.mediaType || media.mediaType === "image") &&
              materialGroup.allTokens.some((token) => this.mediaContainsGalleryToken(media, token));
          });
          if (fallbackIndex != null) {
            const fallbackMedia = this.getMediaByIndex(fallbackIndex);
            const fallbackGroups = galleryGroups.map((group) => group !== materialGroup ? group : {
              ...group,
              activeTokens: group.allTokens.filter((token) => this.mediaContainsGalleryToken(fallbackMedia, token)),
            });
            const fallbackGallery = {
              source: fabricGroup ? "initial_fabric_fallback" : "initial_material_fallback",
              mediaIndexes: this.filterMediaIndexesByGroups(defaultIndexes, fallbackGroups),
              groups: fallbackGroups,
              warning: "",
            };
            if (fabricGroup) this.lastFabricGallery = fallbackGallery;
            else this.lastMaterialGallery = fallbackGallery;
            return fallbackGallery;
          }
        }
        const gallery = {
          source: "gallery_token_groups",
          mediaIndexes: groupedIndexes,
          groups: galleryGroups,
          warning: groupedIndexes.length
            ? ""
            : "Gallery token groups matched no assigned or neutral media.",
        };
        if (hasMaterialMedia && groupedIndexes.some((index) => {
          return materialGroup.activeTokens.some((token) => this.mediaContainsGalleryToken(this.getMediaByIndex(index), token));
        })) {
          if (fabricGroup) this.lastFabricGallery = gallery;
          else this.lastMaterialGallery = gallery;
        }
        return gallery;
      }

      if (this.mode === "native") {
        if (this.hasSelectedLinkedFabricOption()) {
          return defaultGallery();
        }

        const featuredMedia = this.state.variant?.featuredMedia || null;
        const featuredIndex = this.toMediaIndex(featuredMedia?.position);

        if (featuredIndex != null) {
          const featuredIndexes = this.uniqueMediaIndexes([featuredIndex]);
          if (!featuredIndexes.length) {
            return defaultGallery("default", "Variant featured media was not found in product gallery.");
          }

          return {
            source: "variant_featured_media",
            mediaIndexes: featuredIndexes,
            warning: "",
          };
        }

        if (featuredMedia) {
          return defaultGallery("default", "Variant featured media was not found in product gallery.");
        }

        return defaultGallery();
      }

      return defaultGallery();
    }

    hasSelectedLinkedFabricOption() {
      return this.optionConfigs().some((option) => {
        const selectedValue = this.state.nativeOptions[option.name] || option.selectedValue || "";
        return Boolean(this.findOptionValue(option.name, selectedValue)?.linkedFabric);
      });
    }

    getDefaultMediaIndexes() {
      return (this.config.media?.items || [])
        .map((media) => this.toMediaIndex(media.position))
        .filter((index) => index != null);
    }

    buildGalleryGroups(mediaItems = this.config.media?.items || []) {
      return [
        this.buildFabricGalleryGroup(),
        ...this.buildNativeOptionGalleryGroups(mediaItems),
      ].filter((group) => group && group.activeTokens.length && group.allTokens.length);
    }

    reconcileGalleryPair(changedOption) {
      const groups = this.buildGalleryGroups();
      // Only explicit two-dimensional photo assignments can drive a pair.
      // Unphotographed choices and neutral photos retain the existing fallback.
      if (groups.length !== 2) return;
      const changed = groups.find((group) => (group.optionName || group.code) === changedOption);
      if (!changed) return;
      const other = groups.find((group) => group !== changed);
      const matches = (media, tokens) => tokens.some((token) => this.mediaContainsGalleryToken(media, token));
      const assigned = (this.config.media?.items || []).filter((media) => {
        return groups.every((group) => matches(media, group.allTokens));
      });
      if (assigned.some((media) => groups.every((group) => matches(media, group.activeTokens)))) return;
      const candidates = assigned.filter((media) => matches(media, changed.activeTokens));
      if (!candidates.length) return;
      const isFabric = other.code === "fabric" && !other.optionName;
      const choices = isFabric
        ? this.getSelectionGroup("upholstery")?.choices || []
        : this.optionConfigs().find((option) => option.name === other.optionName)?.values || [];
      const tokensFor = (choice) => isFabric
        ? this.getFabricGalleryTokens(choice, this.getFabricMediaTokenMap())
        : this.getNativeOptionValueGalleryTokens(choice, other.optionName);
      // Ambiguous aliases must never silently select a different configuration.
      const choicesFor = (media) => choices.filter((choice) => matches(media, tokensFor(choice)));
      const pair = candidates.map(choicesFor).find((values) => values.length === 1);
      if (!pair) return;
      if (isFabric) {
        this.state.fabric = this.getFabricChoice(pair[0].handle);
        this.applyFabricToNativeState(this.state.fabric, this.state.nativeOptions);
      } else {
        this.state.nativeOptions[other.optionName] = pair[0].name;
      }
    }

    buildFabricGalleryGroup() {
      if (this.mode !== "upholstery_hybrid" || !this.state.fabric?.handle) return null;

      const upholsteryGroup = this.getSelectionGroup("upholstery");
      const choices = upholsteryGroup?.choices || [];
      if (!choices.length) return null;

      const tokenMap = this.getFabricMediaTokenMap();
      const allTokens = [];
      const activeTokens = [];

      choices.forEach((choice) => {
        const tokens = this.getFabricGalleryTokens(choice, tokenMap);
        allTokens.push(...tokens);

        if (choice.handle === this.state.fabric.handle) {
          activeTokens.push(...tokens);
        }
      });

      return {
        code: "fabric",
        source: Object.prototype.hasOwnProperty.call(tokenMap, this.state.fabric.handle)
          ? "fabric_media_map"
          : "auto_fabric_tokens",
        activeTokens: this.uniqueTokens(activeTokens),
        allTokens: this.uniqueTokens(allTokens),
      };
    }

    buildNativeOptionGalleryGroups(mediaItems = this.config.media?.items || []) {
      const priceGroupName = this.mode === "upholstery_hybrid"
        ? this.config.nativeOptions.priceGroupOptionName
        : "";

      return this.optionConfigs().filter((option) => {
        return !priceGroupName || option.name !== priceGroupName;
      }).map((option) => {
        const selectedValue = this.state.nativeOptions[option.name] || option.selectedValue || "";
        if (!selectedValue) return null;
        const selectedValueConfig = this.findOptionValue(option.name, selectedValue);

        return {
          code: this.normalizeGalleryToken(option.name),
          source: selectedValueConfig?.linkedFabric ? "native_linked_fabric" : "native_options",
          isNonFabricMaterial: selectedValueConfig?.linkedFabric?.isFabric === false,
          optionName: option.name,
          activeTokens: this.getNativeOptionValueGalleryTokens(selectedValueConfig || selectedValue, option.name),
          allTokens: this.uniqueTokens((option.values || []).flatMap((value) => {
            return this.getNativeOptionValueGalleryTokens(value, option.name);
          })),
        };
      }).filter((group) => {
        if (!group) return false;

        return mediaItems.some((media) => {
          return group.allTokens.some((token) => this.mediaContainsGalleryToken(media, token));
        });
      });
    }

    getNativeOptionValueGalleryTokens(value, optionName) {
      // Resolved in Liquid from the global option-alias dictionary.
      if (Array.isArray(value?.galleryTokens) && value.galleryTokens.length) {
        return this.normalizeTokenInput(value.galleryTokens);
      }

      const overrideTokens = this.getGalleryFilterOptionOverrideTokens(
        optionName,
        value?.name || value,
      );
      if (overrideTokens.length) return overrideTokens;

      const linkedFabric = value?.linkedFabric;
      if (!linkedFabric) {
        return this.buildGalleryTokenAliases(value?.name || value);
      }

      return this.getFabricGalleryTokens(linkedFabric, this.getFabricMediaTokenMap());
    }

    getGalleryFilterOptionOverrideTokens(optionName, value) {
      const normalizedOptionName = this.normalizeGalleryToken(optionName);
      const normalizedValue = this.normalizeGalleryToken(value);
      if (!normalizedOptionName || !normalizedValue) return [];

      return this.config.media?.galleryFilterOptionOverrides.flatMap((entry) => {
        if (typeof entry !== "string") return [];

        const [overrideOptionName, overrideValue, ...overrideTokens] = entry.split("|").map((part) => part.trim());
        if (this.normalizeGalleryToken(overrideOptionName) !== normalizedOptionName ||
          this.normalizeGalleryToken(overrideValue) !== normalizedValue) {
          return [];
        }

        return this.normalizeTokenInput(overrideTokens.join("|"));
      }) || [];
    }

    getMediaByIndex(index) {
      const targetIndex = this.toMediaIndex(index);
      if (targetIndex == null) return null;
      return (this.config.media?.items || []).find((media) => this.toMediaIndex(media.position) === targetIndex) || null;
    }

    filterMediaIndexesByGroups(indexes, groups) {
      return indexes.filter((index) => {
        const media = this.getMediaByIndex(index);
        if (!media) return false;

        return this.mediaMatchesGalleryGroups(media, groups);
      });
    }

    mediaMatchesGalleryGroups(media, groups) {
      return groups.every((group) => {
        const containsGroupToken = group.allTokens.some((token) => this.mediaContainsGalleryToken(media, token));
        if (!containsGroupToken) return true;

        return group.activeTokens.some((token) => this.mediaContainsGalleryToken(media, token));
      });
    }

    mediaContainsGalleryToken(media, token) {
      const fileBase = String(media?.fileBase || "").toLowerCase();
      const normalizedToken = this.normalizeGalleryToken(token);
      if (!fileBase || !normalizedToken) return false;

      const normalizedFileBase = this.normalizeGalleryToken(fileBase);
      const normalizedSegments = fileBase
        .split("_")
        .map((segment) => this.normalizeGalleryToken(segment))
        .filter(Boolean);

      if (normalizedSegments.includes(normalizedToken)) return true;
      if (this.normalizedFileContainsToken(normalizedFileBase, normalizedToken)) return true;

      const compactToken = normalizedToken.replace(/-/g, "");
      if (compactToken !== normalizedToken) {
        return normalizedFileBase.replace(/-/g, "").includes(compactToken);
      }

      return false;
    }

    normalizedFileContainsToken(fileBase, token) {
      return fileBase === token ||
        fileBase.startsWith(`${token}-`) ||
        fileBase.endsWith(`-${token}`) ||
        fileBase.includes(`-${token}-`);
    }

    getFabricMediaTokenMap() {
      const map = this.config.media?.fabricMediaTokenMap || {};
      return map && typeof map === "object" && !Array.isArray(map) ? map : {};
    }

    normalizeFabricMediaTokenMap(value) {
      if (!value) return {};

      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          return this.normalizeFabricMediaTokenMap(parsed);
        } catch (error) {
          console.warn("[ProductConfigurator] Invalid fabric_media_map JSON. Using auto fabric tokens.", error);
          return {};
        }
      }

      if (typeof value !== "object" || Array.isArray(value)) return {};

      return value;
    }

    getFabricGalleryTokens(fabric, tokenMap) {
      if (Object.prototype.hasOwnProperty.call(tokenMap, fabric.handle)) {
        return this.normalizeTokenInput(tokenMap[fabric.handle]);
      }

      return this.uniqueTokens([
        ...this.buildGalleryTokenAliases([
          fabric.handle,
          fabric.label,
          fabric.mediaKey,
        ]),
        ...this.normalizeTokenInput(fabric.mediaAliases),
      ]);
    }

    normalizeTokenInput(value) {
      if (Array.isArray(value)) return this.uniqueTokens(value.flatMap((item) => this.buildGalleryTokenAliases(item)));
      if (typeof value === "string") {
        return this.uniqueTokens(value
          .split(/[|,\r\n]+/)
          .flatMap((item) => this.buildGalleryTokenAliases(item)));
      }
      if (value && typeof value === "object") {
        if (Array.isArray(value.tokens)) return this.normalizeTokenInput(value.tokens);
        if (Array.isArray(value.gallery_tokens)) return this.normalizeTokenInput(value.gallery_tokens);
        if (Array.isArray(value.media_tokens)) return this.normalizeTokenInput(value.media_tokens);
        if (value.token) return this.buildGalleryTokenAliases(value.token);
        if (value.media_token) return this.buildGalleryTokenAliases(value.media_token);
      }

      return this.buildGalleryTokenAliases(value);
    }

    buildGalleryTokenAliases(value) {
      const values = Array.isArray(value) ? value : [value];
      const tokens = [];

      values.forEach((item) => {
        const token = this.normalizeGalleryToken(item);
        if (!token) return;

        tokens.push(token);
        tokens.push(token.replace(/-/g, ""));
        tokens.push(token.replace(/-/g, "_"));
      });

      return this.uniqueTokens(tokens);
    }

    uniqueTokens(tokens) {
      const seen = new Set();
      return tokens
        .map((token) => String(token || "").trim())
        .filter((token) => {
          if (!token || seen.has(token)) return false;
          seen.add(token);
          return true;
        });
    }

    uniqueMediaIndexes(indexes) {
      const knownIndexes = new Set(this.getDefaultMediaIndexes());
      const seen = new Set();

      return indexes
        .map((index) => this.toMediaIndex(index))
        .filter((index) => {
          if (index == null || !knownIndexes.has(index) || seen.has(index)) return false;
          seen.add(index);
          return true;
        });
    }

    toMediaIndex(value) {
      const number = Number(value);
      return Number.isInteger(number) && number >= 0 ? number : null;
    }

    normalizeGalleryToken(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\u0142/g, "l")
        .replace(/\u0141/g, "l")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    }

    warnGallery(gallery) {
      if (!gallery?.warning) return;
      const warningKey = `${gallery.source}:${gallery.warning}`;
      if (this.galleryWarningKey === warningKey) return;

      this.galleryWarningKey = warningKey;
      console.warn("[ProductConfigurator] Gallery warning:", gallery.warning, gallery);
    }

    getInitialAddonValue(addonGroup, urlState) {
      if (!this.isProductBackedAddonGroup(addonGroup)) {
        const choices = Array.isArray(addonGroup.choices) ? addonGroup.choices : [];
        const candidates = [
          urlState.addons[addonGroup.handle],
          addonGroup.defaultValue,
          this.getCheckedAddonInputValue(addonGroup.handle),
        ];
        for (const value of candidates) {
          if (!value) continue;
          const choice = choices.find((item) => item.value === value || item.code === value);
          if (addonGroup.required && (choice?.value === "none" || !this.isAddonChoiceSelectable(choice, addonGroup))) continue;
          if (choice) return choice.value;
        }
        if (addonGroup.required) {
          return choices.find((choice) => choice.value !== "none" && this.isAddonChoiceSelectable(choice, addonGroup))?.value || "none";
        }
        return "none";
      }

      const candidates = [
        urlState.addons[addonGroup.handle],
        addonGroup.defaultValue,
        this.getCheckedAddonInputValue(addonGroup.handle),
      ];

      // Absent/invalid candidates must fall through to the next one (docs 11.3
      // precedence: URL -> default_option_code -> DOM). resolveAddonStateValue
      // collapses them to "none", which would shadow the merchant default, so a
      // candidate only wins when the resolver returns it unchanged.
      for (const value of candidates) {
        const stringValue = String(value || "").trim();
        if (!stringValue) continue;
        const normalized = this.resolveAddonStateValue(addonGroup, stringValue);
        if (normalized === stringValue) return normalized;
      }

      return this.resolveAddonStateValue(addonGroup, "none");
    }

    resolveAddonStateValue(addonGroup, value) {
      if (!addonGroup) return "none";

      if (!this.isProductBackedAddonGroup(addonGroup)) {
        if (addonGroup.variantScoped) {
          const choice = addonGroup.choices?.find((item) => item.value === value);
          if (this.isAddonChoiceSelectable(choice, addonGroup)) return choice.value;
          return addonGroup.required ? addonGroup.defaultValue : "none";
        }
        return this.getAddonChoice(addonGroup.handle, value || "none")?.value || "none";
      }

      const stringValue = String(value || "").trim();
      if (!stringValue) return addonGroup.required ? "" : "none";

      const choices = Array.isArray(addonGroup.choices) ? addonGroup.choices : [];
      const choice = choices.find((item) => item.value === stringValue);
      if (this.isAddonChoiceSelectable(choice, addonGroup)) return choice.value;

      return addonGroup.required ? "" : "none";
    }

    reconcileAddonState(previousConfig = null) {
      const nextAddons = {};
      const removedLabels = [];
      const restoredLabels = [];

      this.config.addonGroups.forEach((addonGroup) => {
        let currentValue = this.state.addons[addonGroup.handle] || "none";
        if (addonGroup.variantScoped && previousConfig && !this.isHandlingPopState && currentValue !== "none") {
          const previousGroup = previousConfig.addonGroups.find((group) => group.handle === addonGroup.handle);
          const previousChoice = previousGroup?.choices.find((choice) => choice.value === currentValue);
          const matchingChoices = addonGroup.choices.filter((choice) => choice.productId && choice.productId === previousChoice?.productId);
          if (matchingChoices.length === 1) currentValue = matchingChoices[0].value;
        }
        let nextValue = this.resolveAddonStateValue(addonGroup, currentValue);

        if (nextValue !== currentValue && currentValue !== "none" && this.isProductBackedAddonGroup(addonGroup)) {
          // Announce only values that exist as real choices; a raw history
          // value unknown to the config resets silently like initial state.
          const removedChoice = this.getAddonChoice(addonGroup.handle, currentValue);
          if (removedChoice) {
            removedLabels.push(removedChoice.label || currentValue);
            this.droppedAddons[addonGroup.handle] = currentValue;
          }
        }

        // Restore an auto-dropped choice once it resolves again, but only on
        // live interaction — history navigation replays the URL exactly.
        const droppedValue = this.droppedAddons[addonGroup.handle];
        if (
          droppedValue &&
          !this.isHandlingPopState &&
          (nextValue === "none" || nextValue === "") &&
          this.isProductBackedAddonGroup(addonGroup) &&
          this.resolveAddonStateValue(addonGroup, droppedValue) === droppedValue
        ) {
          nextValue = droppedValue;
          delete this.droppedAddons[addonGroup.handle];
          const restoredChoice = this.getAddonChoice(addonGroup.handle, droppedValue);
          restoredLabels.push(restoredChoice?.label || droppedValue);
        }

        nextAddons[addonGroup.handle] = nextValue;
      });

      this.state.addons = nextAddons;

      if (removedLabels.length) this.announceAddonRemoval(removedLabels);
      if (restoredLabels.length) this.announceAddonRestore(restoredLabels);
    }

    // The inline status line sits low in the drawer and is easy to miss, so a
    // cart failure additionally opens the message-window popup. The status
    // line stays rendered as the persistent record after the popup auto-closes
    // and as the fallback when the section is absent from the layout.
    announceCartError(message) {
      if (!message) return;

      const popup = document.getElementById("message-cart-update-error");
      const text = popup?.querySelector("[data-cart-update-error-message]");
      if (!popup || !text) return;

      text.textContent = message;
      window.dispatchEvent(new CustomEvent("messageOpen", { detail: { id: "message-cart-update-error" } }));
    }

    announceAddonRemoval(removedLabels) {
      const template = this.selectors.status?.dataset.addonRemoved || "";
      if (!template) return;
      const message = template.replace("__LABEL__", removedLabels.join(", "));

      const popup = document.getElementById("message-addon-removed");
      const text = popup?.querySelector("[data-addon-removed-message]");
      if (popup && text) {
        text.textContent = message;
        window.dispatchEvent(new CustomEvent("messageOpen", { detail: { id: "message-addon-removed" } }));
        return;
      }

      // No popup section in the layout: fall back to the inline status line.
      this.addonRemovedNotice = message;
    }

    announceAddonRestore(restoredLabels) {
      const template = this.selectors.status?.dataset.addonRestored || "";
      if (!template) return;
      const message = template.replace("__LABEL__", restoredLabels.join(", "));

      const popup = document.getElementById("message-addon-restored");
      const text = popup?.querySelector("[data-addon-restored-message]");
      if (popup && text) {
        text.textContent = message;
        window.dispatchEvent(new CustomEvent("messageOpen", { detail: { id: "message-addon-restored" } }));
        return;
      }

      // No popup section in the layout: fall back to the shared notice slot —
      // the restore must never be silent because it changes the total price.
      this.addonRemovedNotice = message;
    }

    isProductBackedAddonGroup(addonGroup) {
      if (!addonGroup) return false;
      if (addonGroup.hasProductBackedChoices === true) return true;

      const choices = Array.isArray(addonGroup.choices) ? addonGroup.choices : [];
      return choices.some((choice) => choice.cartLineMode === "standalone_product");
    }

    isAddonChoiceSelectable(choice, addonGroup) {
      if (!choice) return false;

      if (choice.value === "none") {
        if (this.isProductBackedAddonGroup(addonGroup)) {
          return choice.selectable !== false && addonGroup?.required !== true;
        }
        return choice.available !== false;
      }

      if (choice.cartLineMode === "standalone_product") {
        return choice.selectable === true &&
          choice.available !== false &&
          choice.resolution?.status === "resolved" &&
          Boolean(choice.variantId);
      }

      return choice.available !== false && Boolean(choice.variantId);
    }

    syncAddonDataset(element, choice) {
      if (!element || !choice) return;

      element.dataset.variantId = choice.variantId ? String(choice.variantId) : "";
      element.dataset.addonChoiceLabel = choice.label || "";
      element.dataset.addonChoiceCode = choice.code || "";
      element.dataset.addonPropertyLabel = choice.propertyLabel || "";
      element.dataset.price = String(choice.priceContribution ?? choice.price ?? 0);
      element.dataset.available = choice.available === false ? "false" : "true";
      element.dataset.cartLineMode = choice.cartLineMode || "";
      element.dataset.resolutionStatus = choice.resolution?.status || "";
    }

    getAddonValidationMessage() {
      return this.getAddonValidationState().message;
    }

    // `announce: true` marks blocking states that arise without an action in
    // that section (an option or a whole group turning unavailable) — those
    // surface as a popup. The neutral choose-a-value prompt stays inline-only.
    getAddonValidationState() {
      const unavailableMessage = this.selectors.status?.dataset.unavailable || "";

      for (const addonGroup of this.config.addonGroups) {
        const groupLabel = addonGroup.label || addonGroup.handle;
        const value = this.state.addons[addonGroup.handle] || "none";
        const choice = this.getAddonChoice(addonGroup.handle, value);

        // Edit mode locks standalone sections to "no selection", so they are
        // exempt from validation (a required prompt would deadlock the edit).
        if (this.editConfigKey && this.isProductBackedAddonGroup(addonGroup)) continue;

        if (this.isProductBackedAddonGroup(addonGroup) || addonGroup.variantScoped) {
          if (value === "none" && addonGroup.required !== true) continue;
          if (addonGroup.status && addonGroup.status !== "ready") {
            return {
              message: this.getAddonGroupUnavailableMessage(groupLabel) || unavailableMessage,
              announce: true,
            };
          }
          if (value === "none") {
            // Required group with nothing chosen yet: ask for a choice instead
            // of showing the sold-out style message.
            const template = this.selectors.status?.dataset.chooseAddon || "";
            if (template) return { message: template.replace("__LABEL__", groupLabel), announce: false };
            return { message: unavailableMessage, announce: false };
          }
          if (!this.isAddonChoiceSelectable(choice, addonGroup)) {
            return {
              message: this.getAddonChoiceUnavailableMessage(choice, value, groupLabel) || unavailableMessage,
              announce: true,
            };
          }
          continue;
        }

        if (choice && value !== "none" && choice.available === false) {
          return {
            message: this.getAddonChoiceUnavailableMessage(choice, value, groupLabel) || unavailableMessage,
            announce: true,
          };
        }
      }

      return { message: "", announce: false };
    }

    // Blocking states that appear on their own surface as a popup once per
    // occurrence; the status line stays as the persistent record (the same
    // pattern as cart errors and add-on removal/restore).
    announceAddonValidation(message) {
      if (!message) {
        this.announcedValidationMessage = "";
        return;
      }
      if (this.announcedValidationMessage === message) return;
      this.announcedValidationMessage = message;

      const popup = document.getElementById("message-addon-unavailable");
      const text = popup?.querySelector("[data-addon-unavailable-message]");
      if (!popup || !text) return;

      text.textContent = message;
      window.dispatchEvent(new CustomEvent("messageOpen", { detail: { id: "message-addon-unavailable" } }));
    }

    // A blocked submit must name the step the customer has to change; the
    // generic unavailable text stays reserved for the base variant, so its
    // meaning becomes unambiguous.
    getAddonChoiceUnavailableMessage(choice, choiceValue, groupLabel) {
      const template = this.selectors.status?.dataset.addonChoiceUnavailable || "";
      if (!template) return "";
      return template
        .replace("__CHOICE__", choice?.label || choiceValue)
        .replace("__GROUP__", groupLabel);
    }

    getAddonGroupUnavailableMessage(groupLabel) {
      const template = this.selectors.status?.dataset.addonGroupUnavailable || "";
      if (!template) return "";
      return template.replace("__LABEL__", groupLabel);
    }

    getAddonResolutionMessage(choice) {
      const status = choice?.resolution?.status || "";
      if (!status || status === "resolved" || status === "not_applicable") return "";
      if (status === "unavailable") {
        // The card itself is fine — only this variant cannot be bought for
        // the current configuration; the generic text implied a broken config.
        return this.selectors.status?.dataset.addonCardUnavailable ||
          this.selectors.status?.dataset.unavailable || "";
      }
      return this.selectors.status?.dataset.unavailable || "";
    }

    getAddonVariantSummary(choice) {
      const options = choice?.resolvedVariant?.selectedOptions;
      if (!options || typeof options !== "object") return "";

      return Object.values(options)
        .map((value) => String(value || "").trim())
        .filter((value) => value && value !== "Default Title")
        .join(" / ");
    }

    warnAddonResolution(addonGroup, choice) {
      const status = choice?.resolution?.status || "";
      if (status !== "ambiguous" && status !== "invalid_config") return;

      const warningKey = `${addonGroup?.handle || ""}:${choice?.value || ""}:${status}`;
      if (this.productAddonWarnings.has(warningKey)) return;

      this.productAddonWarnings.add(warningKey);
      console.warn("[ProductConfigurator] Product-backed add-on is not selectable", {
        group: addonGroup?.handle || "",
        choice: choice?.value || "",
        status,
        resolution: choice?.resolution || null,
      });
    }

    getBaseProperties(configKey) {
      const properties = {
        _config_key: configKey,
        _configurator_mode: this.mode,
      };
      const fabric = this.state.fabric;

      if (this.mode === "upholstery_hybrid") {
        const gallery = this.resolveGallery();
        const image = gallery.mediaIndexes
          .map((index) => this.getMediaByIndex(index))
          .find((media) => media?.mediaType === "image");
        this.setProperty(properties, "_configuration_media_id", image?.id || "");
      }

      if (fabric) {
        this.setProperty(properties, this.getPropertyLabel("fabric", "Tkanina"), fabric.label);
        this.setProperty(properties, this.getPropertyLabel("fabricFamily", "Rodzina tkaniny"), fabric.familyName || fabric.properties?.family || "");
        this.setProperty(properties, this.getPropertyLabel("colorCode", "Kod koloru"), fabric.properties?.colorCode || "");
        this.setProperty(properties, this.getPropertyLabel("grotId", "GROT ID"), fabric.properties?.grotId || fabric.grotId || "");
        this.setProperty(properties, "_fabric_handle", fabric.handle);
        this.setProperty(properties, "_fabric_price_group", fabric.priceGroup);
        this.setProperty(properties, "_fabric_grot_id", fabric.properties?.grotId || fabric.grotId || "");
      }

      this.setProperty(properties, this.getPropertyLabel("shipping", "Czas wysyłki"), this.getShippingTime());

      const addonSummary = this.getAddonSummary();
      if (addonSummary) {
        properties[this.getPropertyLabel("addons", "Dodatki")] = addonSummary;
      }

      return properties;
    }

    getShippingTime() {
      const variantId = String(this.state.variant?.id || "");
      const fabricHandle = this.mode === "upholstery_hybrid" ? this.state.fabric?.handle || "" : "";
      const entries = Array.from(document.querySelectorAll(".shipping-info[data-shipping-time]"))
        .filter((entry) => entry.dataset.variantDetail === variantId);
      const entry = entries.find((item) => fabricHandle && item.dataset.shippingFabric === fabricHandle)
        || entries.find((item) => !item.dataset.shippingFabric);
      return entry?.dataset.shippingTime || "";
    }

    // Visible cart property texts follow the storefront locale via canonical
    // JSON; the inline defaults only guard against a config missing the field.
    getPropertyLabel(key, fallback) {
      return this.config.propertyLabels?.[key] || fallback;
    }

    getConfigKey() {
      const productHandle = this.getProductHandle();
      const variantId = String(this.state.variant?.id || "");
      const fabricHandle = this.state.fabric?.handle || "";
      const fabricPriceGroup = this.state.fabric?.priceGroup || "";
      const addons = this.config.addonGroups
        .map((addonGroup) => {
          if (this.getLockedStandaloneSelection(addonGroup)) return "";
          const value = this.state.addons[addonGroup.handle] || "none";

          // Preserve the legacy fulfilment key. Full-choice cart grouping now
          // also uses _iwc_configuration_key, including standalone selections.
          // Linked variant-backed selections in mixed groups must stay here.
          if (this.isProductBackedAddonGroup(addonGroup)) {
            const choice = this.getAddonChoice(addonGroup.handle, value);
            if (value === "none" || choice?.cartLineMode !== "linked_addon") return "";
          }

          return `${addonGroup.handle}:${value}`;
        })
        .filter(Boolean);

      return [
        this.mode,
        productHandle,
        variantId,
        fabricHandle,
        fabricPriceGroup,
        ...addons,
      ].join("|");
    }

    getAddonSummary() {
      if (!this.config.addonGroups.length) return "";

      return this.config.addonGroups
        .map((addonGroup) => {
          if (this.getLockedStandaloneSelection(addonGroup)) return "";
          const value = this.state.addons[addonGroup.handle] || "none";
          const choice = this.getAddonChoice(addonGroup.handle, value);

          if (this.isProductBackedAddonGroup(addonGroup)) {
            if (value === "none" || choice?.cartLineMode !== "linked_addon") return "";
          }

          if (value !== "none" && choice?.propertyLabel) return choice.propertyLabel;

          const label = value === "none"
            ? this.getPropertyLabel("without", "bez")
            : value === "yes" ? this.getPropertyLabel("with", "tak") : choice?.label || value;

          return `${addonGroup.label || addonGroup.handle}: ${label}`;
        })
        .filter(Boolean)
        .join(", ");
    }

    setProperty(properties, key, value) {
      const stringValue = String(value || "").trim();
      if (stringValue) properties[key] = stringValue;
    }

    getLockedStandaloneSelection(addonGroup) {
      return this.editConfigKey && this.editOfferContext?.selection?.addons?.find((addon) =>
        addon.group_code === addonGroup.handle && addon.cart_line_mode === "standalone_product");
    }

    getSelectedAddons() {
      return this.config.addonGroups
        .map((addonGroup) => {
          if (this.getLockedStandaloneSelection(addonGroup)) return null;
          const value = this.state.addons[addonGroup.handle] || "none";
          const choice = this.getAddonChoice(addonGroup.handle, value);
          if (!choice || !choice.variantId || value === "none") return null;
          if (this.editConfigKey && choice.cartLineMode === "standalone_product") return null;
          if (choice.cartLineMode === "standalone_product" && !this.isAddonChoiceSelectable(choice, addonGroup)) return null;

          return Object.assign({}, choice, {
            groupHandle: addonGroup.handle,
            groupLabel: addonGroup.label,
          });
        })
        .filter(Boolean);
    }

    getAddonGroup(handle) {
      return this.config.addonGroups.find((addonGroup) => addonGroup.handle === handle) || null;
    }

    getAddonChoice(groupHandle, value) {
      const addonGroup = this.getAddonGroup(groupHandle);
      if (!addonGroup) return null;
      const choices = Array.isArray(addonGroup.choices) ? addonGroup.choices : [];
      const choice = choices.find((item) => item.value === value);
      if (choice) return choice;
      if (this.isProductBackedAddonGroup(addonGroup)) return null;
      return choices[0] || null;
    }

    getCheckedAddonInputValue(groupHandle) {
      const select = this.addonInputs.find((input) => {
        return input.tagName === "SELECT" && input.dataset.addonGroupHandle === groupHandle;
      });
      if (select) return select.value || "none";

      const checked = this.addonInputs.find((input) => {
        return input.dataset.addonGroupHandle === groupHandle && input.checked;
      });
      return checked?.value || "";
    }

    getFabricChoice(handle) {
      const group = this.getSelectionGroup("upholstery");
      const choice = group?.choices?.find((item) => item.handle === handle);
      return choice ? this.hydrateFabricChoice(choice) : null;
    }

    hydrateFabricChoice(choice) {
      const input = this.fabricInputs.find((item) => item.value === choice.handle);
      return Object.assign({}, choice, {
        familyName: input?.dataset.familyName || choice.properties?.family || "",
        familyHandle: input?.dataset.familyHandle || "",
        categoryLabel: input?.dataset.fabricCategoryLabel || "",
        typeLabel: input?.dataset.fabricTypeLabel || choice.typeLabel || "",
        colorLabel: input?.dataset.fabricColorLabel || "",
        isFabric: input?.dataset.fabricIsFabric !== "false",
        grotId: input?.dataset.grotId || choice.properties?.grotId || "",
      });
    }

    getSelectionGroup(code) {
      return this.config.selectionGroups.find((group) => group.code === code) || null;
    }

    getOptionConfig(name) {
      return this.optionConfigs().find((option) => option.name === name) || null;
    }

    findOptionValue(optionName, value) {
      const option = this.getOptionConfig(optionName);
      if (!option || value == null) return null;

      const stringValue = String(value).trim();
      const exact = option.values?.find((item) => String(item.name).trim() === stringValue);
      if (exact) return exact;

      if (optionName === this.config.nativeOptions.priceGroupOptionName) {
        return option.values?.find((item) => this.normalizeGroup(item.name) === this.normalizeGroup(stringValue)) || null;
      }

      return option.values?.find((item) => String(item.name).trim().toLowerCase() === stringValue.toLowerCase()) || null;
    }

    optionConfigs() {
      return this.config.nativeOptions.options || [];
    }

    getProductHandle() {
      return this.config.product?.handle || this.selectors.handle?.value || this.dataset.productHandle || "";
    }

    getQuantity() {
      const quantity = Number(this.selectors.quantity?.value || 1);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    getSiblingHandleFromLink(link) {
      const url = new URL(link.href, window.location.origin);
      const match = this.config.siblingProducts.find((sibling) => {
        return new URL(sibling.url, window.location.origin).pathname === url.pathname;
      });
      return match?.handle || "";
    }

    setResolving(isResolving) {
      this.isResolving = isResolving;
      this.submitButtons.forEach((button) => {
        button.classList.toggle("loading", isResolving);
      });
      if (this.config && this.state) this.renderAddons();
      this.renderStatus();
      if (!isResolving) this.publishOfferView();
    }

    publishOfferView() {
      if (this.isResolving || this.resolveError || this.isApplyingExternalState || this.editContextLoading) return;
      try {
        window.IWCAnalytics?.viewItem(this);
      } catch (_) {
        console.info("[IWC analytics] configurator_view_unavailable");
      }
    }

    setSubmitting(isSubmitting) {
      this.submitButtons.forEach((button) => {
        button.classList.toggle("loading", isSubmitting);
        button.disabled = isSubmitting;
      });
      if (!isSubmitting) this.renderStatus();
    }

    parseOptionValueIds(value) {
      return String(value || "")
        .split(",")
        .map((id) => this.normalizeId(id))
        .filter(Boolean);
    }

    parseTokens(value) {
      return String(value || "")
        .split("|")
        .map((token) => token.trim())
        .filter(Boolean);
    }

    normalizeId(value) {
      const stringValue = String(value || "").trim();
      if (!stringValue) return "";
      return stringValue.split("/").pop();
    }

    normalizeGroup(value) {
      const stringValue = String(value || "").trim();
      if (/^\d+$/.test(stringValue)) return String(Number(stringValue));
      return stringValue.toLowerCase();
    }

    formatPriceDifference(cents) {
      const amount = Number(cents || 0);
      if (!Number.isFinite(amount)) return "";

      if (amount < 0) return `- ${this.formatMoneyWithoutTrailingZeros(Math.abs(amount))}`;
      return `+ ${this.formatMoneyWithoutTrailingZeros(amount)}`;
    }

    formatMoneyWithoutTrailingZeros(cents) {
      return this.formatMoney(cents).replace(/([.,])00(?=\D*$)/, "");
    }

    arrayEqual(first, second) {
      if (!Array.isArray(first) || !Array.isArray(second)) return false;
      if (first.length !== second.length) return false;
      return first.every((value, index) => value === second[index]);
    }

    shallowObjectEqual(first, second) {
      const firstValue = first || {};
      const secondValue = second || {};
      const firstKeys = Object.keys(firstValue);
      const secondKeys = Object.keys(secondValue);

      if (firstKeys.length !== secondKeys.length) return false;
      return firstKeys.every((key) => String(firstValue[key] || "") === String(secondValue[key] || ""));
    }

    escapeAttribute(value) {
      if (window.CSS?.escape) return CSS.escape(value);
      return String(value).replace(/"/g, '\\"');
    }

    formatMoney(cents) {
      const format = window.moneyFormat || "{{amount}}";
      const placeholder = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholder);
      const formatType = match ? match[1] : "amount";

      const formatWithDelimiters = (amount, precision = 2, thousands = ",", decimal = ".") => {
        if (Number.isNaN(amount) || amount == null) return "0";
        const parts = (amount / 100).toFixed(precision).split(".");
        const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
      };

      let value = "";
      if (formatType === "amount") value = formatWithDelimiters(cents, 2);
      if (formatType === "amount_no_decimals") value = formatWithDelimiters(cents, 0);
      if (formatType === "amount_with_comma_separator") value = formatWithDelimiters(cents, 2, ".", ",");
      if (formatType === "amount_no_decimals_with_comma_separator") value = formatWithDelimiters(cents, 0, ".", ",");
      if (formatType === "amount_no_decimals_with_space_separator") value = formatWithDelimiters(cents, 0, " ");
      if (formatType === "amount_with_period_and_space_separator") value = formatWithDelimiters(cents, 2, " ", ".");

      return format.replace(placeholder, value);
    }
  }

  customElements.define("product-configurator", ProductConfigurator);
}
